<script setup>
import { reactive, onMounted, ref, computed, watch, nextTick } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useValidacao } from '@/composables/useValidacao'
import { PED_VEN_ST } from '@/constants/PED_VEN_ST'
import { PED_VEN_WORKFLOW_ST } from '@/constants/PED_VEN_WORKFLOW_ST'
import { PED_VEN_MODALIDADE_VND } from '@/constants/PED_VEN_MODALIDADE_VND'
import { PARCEIRO_VND_PAPEL_COM } from '@/constants/PARCEIRO_VND_PAPEL_COM'
import { useAuthStore } from '@/stores/auth'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import SemAcessoModulo from '@/components/base/util/SemAcessoModulo.vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { templatePrecificacaoSearch } from '@/search-definitions/templatePrecificacaoSearch'
import { criarConfigBuscaCondicaoPagamento } from '@/search-definitions/condicaoPagamentoSearch'
import { criarConfigBuscaParceiroVendedor } from '@/search-definitions/parceiroVendedorSearch'
import { criarConfigBuscaOperacaoVenda } from '@/search-definitions/operacaoVendaSearch'
import { criarConfigBuscaEnderecoParceiro } from '@/search-definitions/parceiroEnderecoSearch'
import { ST } from '@/constants/ST'
import { PED_VEN_MODO_PRECIFIC } from '@/constants/PED_VEN_MODO_PRECIFIC'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM.Custom'
import { criarConfigBuscaPrecoComercialCliente } from '@/search-definitions/precoComercialClienteSearch'
import PedidoVendaItensExcel from './PedidoVendaItensExcel.vue'
import ClausulasGrid from '@/components/clausulas/ClausulasGrid.vue'
import { CLAUSULAS_COM_APLIC } from '@/constants/CLAUSULAS_COM_APLIC'
import { MOE_FONTE_COTACAO } from '@/constants/MOE_FONTE_COTACAO'
import { MOE } from '@/constants/MOE'
import { FIN_ST_TAX_CONVERCAO } from '@/constants/FIN_ST_TAX_CONVERCAO'
import WorkflowAprovacaoGrid from '@/components/base/workflow/aprovacoes/WorkflowAprovacaoGrid.vue'
import { WORK_APR_ST } from '@/constants/WORK_APR_ST'
import { formatarDataBR } from '@/utils/useDataBR'
import { ESTQ_RESERVA_ORIGEM } from '@/constants/ESTQ_RESERVA_ORIGEM'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idPedidoVenda: 0,
      idOrganizacao: null,
      idFilial: null,
      numeroPedidoVenda: '',
      lstStatusPedido: '',
      lstModalidadeVenda: '',
      lstModoPrecificacao: PED_VEN_MODO_PRECIFIC.PrecificacaoPorTabela,
      idPrecoComercial: null,
      dataEmissao: new Date().toISOString().split('T')[0],
      dataValidadeOrcamento: null,
      dataFechamento: null,
      idParceiroNegocioCliente: null,
      idCondicaoPagamento: null,
      lstFormaPagamento: '',
      lstFormaPagamentoInternacional: '',
      idParceiroIndicador: null,
      atributosPrecificacaoJson: null,
      operacaoFinanciada: false,
      observacaoInterna: '',
      valorTotalOriginal: 0,
      valorTotalFrete: 0,
      valorTotalDesconto: 0,
      valorTotalNegociado: 0,
      margemEsperadaPercentual: null,
      percentualTolerancia: null,
      lstMoeda: '',
      lstStatusWorkflowPedido: '',
      lstStatusAprovacaoComercial: '',
      lstMoedaGerencial: '',
      dataAlvoBuscaCambio: null,
      dataReferenciaCambio: null,
      taxaCambioGerencialPadrao: null,
      lstTaxaCambioStatus: '',
      lstFonteCambio: '',
      idOperacao: null,
      idParceiroEnderecoFaturamento: null,
      idParceiroRemessa: null,
      idParceiroEnderecoEntrega: null,
      idContratoVenda: null,
      idTemplatePrecificacao: null,
      lstMoedaPadrao: '',
      taxaCambioPadrao: null,
      taxaCambioGerencial: null,
      lstTipoTaxaConversao: '',
      valorTotalOriginalPadrao: null,
      valorTotalOriginalGerencial: null,
      valorTotalFretePadrao: null,
      valorTotalFreteGerencial: null,
      valorTotalDescontoPadrao: null,
      valorTotalDescontoGerencial: null,
      valorTotalNegociadoPadrao: null,
      valorTotalNegociadoGerencial: null,
      lstIncoterm: '',
      lstLocalIncoterm: ''
    })
  },
  contextoPai: { type: Object, default: () => ({}) },
  drilldownMode: { type: Boolean, default: false },
  pipelineProps: { type: Object, default: () => ({}) },
  funcao: {
    type: String,
    default: 'edicao',
    validator: (v) => ['edicao', 'adicao'].includes(v)
  },
  // prePreencher: null = modo espera (pergunta ao usuário),
  // true = pré-preenche dados do contrato,
  // false = adição sem pré-preenchimento,
  // undefined = fora do fluxo de contrato (PedidoVendaAdministrar)
  prePreencher: {
    default: undefined
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification', 'drilldown-pop', 'pipeline-navegar', 'drilldown-push', 'update:funcao'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const listas = reactive({})
const mostrarAuditoria = ref(false)
const abaAtiva = ref('comercial')
const confirmationModalRef = ref(null)
const confirmacaoEncerrarRef = ref(null)
const confirmacaoReabrirRef = ref(null)
const confirmacaoTransicaoRef = ref(null)
const pendingAction = ref(null)
const transicaoPendente = ref(null)
const itensPedido = ref([])
const idFilialPadrao = ref(null)
const idLocalEstoquePadrao = ref(null)
const descricaoLocalEstoquePadrao = ref('')
const dataPrevisaoEntregaPadrao = ref(null)
const condicoesPagamento = ref([])
const isLoadingCondicoes = ref(false)
const isLoadingAcao = ref(false)
const condicaoEmEdicao = ref(null)
const showModalCondicao = ref(false)
const modalExclusaoCondicaoRef = ref(null)
const modalExclusaoCondicao = ref({ id: null })
const clausulasGridRef = ref(null)

// ============================================================
// FLAG DE APROVAÇÃO AUTOMÁTICA (lida do Master)
// ============================================================
const isAprovacaoAutomatica = ref(false)

const carregarIsAprovacaoAutomatica = async () => {
  const idEmpresa = authStore.empresaAtiva
  if (!idEmpresa) return
  try {
    const response = await api.get(`/EmpresaParametrosComerciais/empresa/${idEmpresa}`)
    isAprovacaoAutomatica.value = !!response.data?.isAprovacaoAutomatica
  } catch (error) {
    // 404 = empresa sem registro (legada) — assume false (comportamento seguro)
    if (error.response?.status !== 404) {
      console.error('Erro ao carregar flag de aprovação automática:', error)
    }
    isAprovacaoAutomatica.value = false
  }
}

// ============================================================
// WORKFLOW DE APROVAÇÃO
// ============================================================
const workflowGridRef = ref(null)
const processosAprovacao = [
  { chave: 'WORK_APR_PROCESSO3', label: 'Aprovação Comercial', chavePermissao: 'APROVACAO_VENDAS_COMERCIAL_APROVAR' },
  { chave: 'WORK_APR_PROCESSO4', label: 'Aprovação de Crédito', chavePermissao: 'APROVACAO_VENDAS_CREDITO_APROVAR' },
  { chave: 'WORK_APR_PROCESSO5', label: 'Reanálise Comercial Por Financiamento de PV', chavePermissao: 'APROVACAO_VENDAS_COMERCIAL_APROVAR' }
]

const podeSolicitarAprovacao = computed(() => {
  return isPedidoSaved.value && podeEditar.value
})

const handleSolicitacaoPreenchida = (payload) => {
  // Momento 2: solicitação preenchida, aguarda disparo
  emit('show-notification', {
    message: `Solicitação de ${payload.processo} preenchida. Clique em "Disparar" para enviar.`,
    type: 'info'
  })
}

// A validacao de regras para envio à Aprovação Comercial é feita pelo
// BACKEND (fonte da verdade). O endpoint `solicitar-aprovacao-comercial`
// retorna ValidationErrorResponse e o `processarErrosValidacaoPV` destaca
// todos os campos com erro, navega para a aba/accordion correta e foca no
// primeiro erro — sem bloquear previamente o envio no frontend.
const handleDispararSolicitacaoComercial = async () => {
  if (!podeEditar.value || isLoading.value) return
  if (!item.value.idPedidoVenda) return

  isLoading.value = true
  try {
    // Persiste o pedido antes de enviar (garante que o backend valide os dados atuais)
    const payload = { ...item.value }
    // Itens são gerenciados pelo endpoint dedicado /PedidoVendaProdutos.
    delete payload.itens
    await api.put(`/PedidoVendas/${item.value.idPedidoVenda}`, payload)
  } catch (putError) {
    const msg = putError.response?.data?.message || 'Erro ao salvar o pedido antes da aprovação.'
    emit('show-notification', { message: msg, type: 'error' })
    isLoading.value = false
    return
  }

  try {
    await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/solicitar-aprovacao-comercial`, {})

    item.value.lstStatusWorkflowPedido = PED_VEN_WORKFLOW_ST.EmAprovacao
    emit('show-notification', { message: 'Pedido enviado para aprovação comercial com sucesso!', type: 'success' })
    // Não emite 'itemAtualizado' aqui: o formulário permanece aberto para o usuário
    // acompanhar o resultado (Indicador read-only e grid de Aprovações).
    if (workflowGridRef.value) workflowGridRef.value.recarregar()
    // Os dados já foram persistidos (PUT + POST): re-sincroniza a baseline do formulário
    // para que `hasChanges` volte a false (evita o modal de alterações não salvas ao cancelar).
    initializeForm(item.value)
  } catch (postError) {
    const msg = processarErrosValidacaoPV(postError.response?.data) || 'Erro ao solicitar aprovação comercial.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleGerarOrcamento = async () => {
  if (!podeEditar.value || isLoading.value) return
  if (!item.value.idPedidoVenda) return

  isLoading.value = true
  try {
    // Persiste o pedido antes de gerar o orçamento (garante dados atuais no backend).
    const payload = { ...item.value }
    delete payload.itens
    await api.put(`/PedidoVendas/${item.value.idPedidoVenda}`, payload)

    const response = await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/gerar-orcamento`, {})
    item.value.lstStatusWorkflowPedido = response.data?.lstStatusWorkflowPedido || PED_VEN_WORKFLOW_ST.Orcamento
    item.value.lstStatusAprovacaoComercial = response.data?.lstStatusAprovacaoComercial || item.value.lstStatusAprovacaoComercial
    emit('show-notification', { message: 'Orçamento gerado com aprovação comercial automática!', type: 'success' })
    if (workflowGridRef.value) workflowGridRef.value.recarregar()
    // Os dados já foram persistidos (PUT + POST): re-sincroniza a baseline do formulário
    // para que `hasChanges` volte a false (evita o modal de alterações não salvas ao cancelar).
    initializeForm(item.value)
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao gerar orçamento.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// ============================================================
// REGIME DA OPERAÇÃO (Nacional vs Exportação)
// ============================================================
const operacaoSelecionada = ref(null)

const onOperacaoSelecionada = (operacao) => {
  operacaoSelecionada.value = operacao
  item.value.idOperacao = operacao.idOperacao

  if (isExportacao.value) {
    // Modo Exportação
    item.value.lstMoeda = '' // libera para escolha
    item.value.idCondicaoPagamento = null // limpa FK
    item.value.lstFormaPagamento = '' // limpa forma nacional
  } else {
    // Modo Nacional — fixa a moeda no CÓDIGO DA LISTA (MOE.BRL = "MOE1"), não na sigla "BRL"
    item.value.lstMoeda = MOE.BRL
    item.value.lstFormaPagamentoInternacional = '' // limpa forma internacional
  }
}

// --- Lógica de Pricing (Template de Precificação) ---
const btnTemplate = ref(null)
const modalBuscaTemplate = ref(false)
const templateTemporario = ref(null)
const pricingFields = ref([])
const valoresPricing = reactive({})
const modalConfirmacaoTrocaTemplateRef = ref(null)

// ============================================================
// FUNÇÃO DO COMPONENTE (Edição / Adição)
// ============================================================
const funcaoInterna = ref(props.funcao)

watch(() => props.funcao, (v) => {
  funcaoInterna.value = v
})

const alterarFuncao = (nova) => {
  funcaoInterna.value = nova
  emit('update:funcao', nova)
}

// ============================================================
// MODO DE ESPERA (prePreencher === null)
// ============================================================
const prePreencherConfirmado = ref(false)

// Inicializa prePreencherValor: null = modo espera, true = pré-preenche, false = adição vazia
// Se está no fluxo do contrato (idContratoEfetivo) e prePreencher não foi passado (undefined)
// ou foi passado como null (vindo do ContratoVendaForm), entra em modo espera (null).
const prePreencherValor = ref(
  (props.prePreencher === undefined || props.prePreencher === null) &&
    (props.contextoPai?.idContratoVenda || props.pipelineProps?.idContrato)
    ? null
    : props.prePreencher
)

// Modo espera: quando prePreencher é null e estamos no fluxo do contrato
const modoEspera = computed(() => {
  return prePreencherValor.value === null && !!idContratoEfetivo.value
})

// Modal de confirmação para pré-preenchimento
const prePreencherModalRef = ref(null)

const iniciarComPrePreencher = () => {
  prePreencherValor.value = true
  prePreencherConfirmado.value = true
  // Pré-preenche dados básicos do contextoPai
  if (props.contextoPai) {
    item.value.idFilial = props.contextoPai.idFilial || null
    item.value.idParceiroNegocioCliente = props.contextoPai.idParceiroNegocioCliente || null
    item.value.lstMoeda = props.contextoPai.lstMoeda || ''
    item.value.atributosPrecificacaoJson = props.contextoPai.atributosPrecificacaoJson || null
    initializeForm(item.value)
  }
  contratosVendaGridAberto.value = false
}

const iniciarSemPrePreencher = () => {
  prePreencherValor.value = false
  prePreencherConfirmado.value = true
  contratosVendaGridAberto.value = false
}

// ============================================================
// ESTADO PARA GRID DE CONTRATOS DE VENDA
// ============================================================
const pedidosDoContrato = ref([])
const isLoadingPedidosContrato = ref(false)
const contratosVendaGridAberto = ref(true)

const idContratoEfetivo = computed(() => {
  return props.contextoPai?.idContratoVenda || props.pipelineProps?.idContrato
})

watch(() => funcaoInterna.value, (novaFuncao) => {
  if (novaFuncao === 'adicao' && prePreencherConfirmado.value) {
    contratosVendaGridAberto.value = false
  }
})

const toggleContratosVendaGrid = () => {
  contratosVendaGridAberto.value = !contratosVendaGridAberto.value
}

const carregarPedidosDoContrato = async () => {
  const idContrato = idContratoEfetivo.value
  if (!idContrato) {
    pedidosDoContrato.value = []
    return
  }
  isLoadingPedidosContrato.value = true
  try {
    const res = await api.get('/PedidoVendas/por-contrato-venda/' + idContrato)
    pedidosDoContrato.value = res.data || []
  } catch (e) {
    console.error('Erro ao carregar pedidos do contrato:', e)
    pedidosDoContrato.value = []
  } finally {
    isLoadingPedidosContrato.value = false
  }
}

const iniciarNovoPedido = () => {
  const action = () => {
    // Limpa dados do formulário
    initializeForm(props.modeloVazio)
    // Garantir que o idContratoVenda seja mantido no novo pedido
    if (idContratoEfetivo.value) {
      item.value.idContratoVenda = idContratoEfetivo.value
      initializeForm(item.value)
    }
    // Limpa dados das abas de itens e pricing
    itensPedido.value = []
    Object.keys(valoresPricing).forEach(key => delete valoresPricing[key])
    pricingFields.value = []
    contratosVendaGridAberto.value = false
    alterarFuncao('adicao')
    emit('itemAtualizado', item.value)

    // Se está no fluxo do contrato e prePreencher ainda não foi definido, mostra o modal
    if (idContratoEfetivo.value && prePreencherValor.value === null) {
      if (prePreencherModalRef.value) {
        prePreencherModalRef.value.show()
      }
    }
  }

  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    action()
  }
}

const selecionarPedido = async (pedido) => {
  if (!pedido?.idPedidoVenda) return
  try {
    const res = await api.get(`/PedidoVendas/${pedido.idPedidoVenda}`)
    const dados = res.data
    initializeForm(dados)
    item.value.idContratoVenda = idContratoEfetivo.value
    isPedidoSaved.value = true
    // Sai do modo espera (prePreencherValor = null) para desbloquear os frames
    prePreencherValor.value = false
    alterarFuncao('edicao')
    emit('itemAtualizado', item.value)
    // Carrega itens e condições
    await carregarItensPedido()
    await carregarCondicoesPagamento()
    await carregarComissoes()
    contratosVendaGridAberto.value = false
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar pedido para edição.', type: 'error' })
  }
}

const salvarEContinuar = async () => {
  if (!podeEditar.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  isLoading.value = true
  const payload = { ...item.value }
  // Itens são gerenciados pelo endpoint dedicado /PedidoVendaProdutos.
  delete payload.itens

  try {
    let res

    if (prePreencherValor.value === true && idContratoEfetivo.value) {
      res = await api.post('/PedidoVendas/from-contrato-venda', {
        idContratoVenda: idContratoEfetivo.value
      })
      emit('show-notification', {
        message: 'Pedido de venda criado com os dados herdados do contrato.',
        type: 'success'
      })
    } else {
      res = item.value.idPedidoVenda
        ? await api.put(`/PedidoVendas/${item.value.idPedidoVenda}`, payload)
        : await api.post('/PedidoVendas', payload)

      emit('show-notification', { message: 'Dados básicos do pedido salvos com sucesso! Agora você pode adicionar itens.', type: 'success' })
    }

    // Preserva campos pré-preenchidos do frontend (ex: lstMoeda herdado do contrato)
    // quando o retorno do backend não os contiver com valor útil.
    const dados = res.data || {}
    const dadosMesclados = { ...item.value, ...dados }
    if (!dadosMesclados.lstMoeda && item.value.lstMoeda) {
      dadosMesclados.lstMoeda = item.value.lstMoeda
    }
    initializeForm(dadosMesclados)
    isPedidoSaved.value = true
    // "Salvar e Continuar" NÃO emite 'itemAtualizado': o formulário permanece aberto
    // para o cadastro dos itens (o pai só é notificado ao Concluir/Cancelar).
  } catch (error) {
    console.error('Erro ao salvar pedido:', error)
    const data = error.response?.data
    let msg = 'Erro ao salvar o pedido.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const sincronizarJsonParaValores = () => {
  try {
    const data = item.value.atributosPrecificacaoJson ? JSON.parse(item.value.atributosPrecificacaoJson) : {}

    Object.keys(data).forEach(key => {
      if (key !== '_templateInfo') {
        valoresPricing[key] = data[key]
      }
    })

    if (data._templateInfo) {
      pricingFields.value = data._templateInfo.atributos || []
    }
  } catch (e) {
    console.error('Erro ao sincronizar JSON de pricing:', e)
  }
}

watch(valoresPricing, (novosValores) => {
  const payload = {
    ...novosValores,
    _templateInfo: {
      atributos: pricingFields.value
    }
  }
  item.value.atributosPrecificacaoJson = JSON.stringify(payload)
}, { deep: true })

const configBuscaTemplate = computed(() => ({
  ...templatePrecificacaoSearch,
  filtrosFixos: {
    lstStatus: ST.Ativo
  }
}))

const abrirBuscaTemplate = () => {
  modalBuscaTemplate.value = true
}

const onTemplateSelecionado = (template) => {
  templateTemporario.value = template
  const temValores = Object.values(valoresPricing).some(v => v !== null && v !== '' && v !== 0)

  if (temValores) {
    if (modalConfirmacaoTrocaTemplateRef.value) {
      modalConfirmacaoTrocaTemplateRef.value.show()
    }
  } else {
    aplicarTemplate(template)
  }
}

const aplicarTemplate = (template) => {
  Object.keys(valoresPricing).forEach(key => delete valoresPricing[key])

  const atributos = template.estruturaJsonSchema ? JSON.parse(template.estruturaJsonSchema) : []
  pricingFields.value = atributos

  atributos.forEach(attr => {
    valoresPricing[attr.chave] = null
  })

  modalBuscaTemplate.value = false
}

const confirmarTrocaTemplate = (confirmed) => {
  if (confirmed && templateTemporario.value) {
    aplicarTemplate(templateTemporario.value)
  }
}

const {
  item,
  isLoading,
  isEditing,
  hasChanges,
  errors,
  focarPrimeiroErro,
  initializeForm
} = useFormulario(props, emit)

// ============================================================
// OPERAÇÃO TRIANGULAR (remessa diferente do cliente)
// ============================================================
const operacaoTriangular = ref(false)

// ConfigBusca para endereço de faturamento (sempre do cliente)
// Usa a search definition parceiroEnderecoSearch que já contém a rota,
// colunas, filtros e campos de busca corretos.
const configBuscaEnderecoFaturamento = computed(() => {
  if (!item.value.idParceiroNegocioCliente) return { endpointApi: '' }
  return criarConfigBuscaEnderecoParceiro(item.value.idParceiroNegocioCliente)
})

// ConfigBusca para endereço de entrega (cliente ou triangular)
// Usa a search definition parceiroEnderecoSearch com o parceiro adequado:
// - Triangular ativa: parceiro de remessa
// - Triangular inativa: cliente comprador
const configBuscaEnderecoEntrega = computed(() => {
  const idParceiro = operacaoTriangular.value
    ? item.value.idParceiroRemessa
    : item.value.idParceiroNegocioCliente
  if (!idParceiro) return { endpointApi: '' }
  return criarConfigBuscaEnderecoParceiro(idParceiro)
})

// Watch: quando ativa/desativa triangular, sincroniza parceiro de remessa
// e limpa endereço de entrega.
// Regra: sem triangular, remessa = próprio cliente (derivado).
watch(() => operacaoTriangular.value, (nova) => {
  if (!nova) {
    // Não-triangular: remessa = cliente (derivado automaticamente)
    item.value.idParceiroRemessa = item.value.idParceiroNegocioCliente || null
    // Limpa endereço de entrega ao desativar triangular (o parceiro mudou)
    item.value.idParceiroEnderecoEntrega = null
  }
})

// Watch: quando troca o parceiro triangular, limpa endereço de entrega
watch(() => item.value.idParceiroRemessa, () => {
  item.value.idParceiroEnderecoEntrega = null
})

// Watch: quando troca o cliente, mantém remessa sincronizada (não-triangular)
watch(() => item.value.idParceiroNegocioCliente, (novoCliente) => {
  if (!operacaoTriangular.value) {
    item.value.idParceiroRemessa = novoCliente || null
  }
})

// isPedidoSaved como ref (não computed) para controle transacional
const isPedidoSaved = ref(false)

// Watch para sincronizar isPedidoSaved com o idPedidoVenda
watch(() => item.value.idPedidoVenda, (newId) => {
  isPedidoSaved.value = !!(newId && newId > 0)
}, { immediate: true })

// ============================================================
// ACCORDION STATE (frames colapsáveis)
// ============================================================
const accordionState = reactive({
  dadosBasicos: true,
  abas: true,
  itens: true,
  clausulas: false
})

// Padrão de validação híbrida: processa erros estruturados do backend
// (ValidationErrorResponse) destacando campos, abrindo accordions/tabs e focando no erro
const { processarErrosBackend: processarErrosValidacaoPV } = useValidacao({
  formErrors: errors,
  accordionState,
  activeTab: abaAtiva,
  fieldTabMap: {
    idFilial: 'dadosBasicos',
    idParceiroNegocioCliente: 'dadosBasicos',
    idParceiroRemessa: 'dadosOperacao',
    idOperacao: 'dadosBasicos',
    lstMoeda: 'dadosBasicos',
    lstMoedaPadrao: 'cambio',
    lstModalidadeVenda: 'dadosBasicos',
    idParceiroEnderecoFaturamento: 'dadosOperacao',
    idParceiroEnderecoEntrega: 'dadosOperacao',
    dataEmissao: 'dadosBasicos',
    lstFormaPagamento: 'comercial',
    lstFormaPagamentoInternacional: 'comercial',
    lstIncoterm: 'dadosOperacao',
    lstLocalIncoterm: 'dadosOperacao',
    idCondicaoPagamento: 'comercial',
    condicoesPagamento: 'comercial',
    atributosPrecificacaoJson: 'pricing',
    itens: 'itens',
    comissoes: 'parceiros',
    clausulas: 'clausulas',
    resumoAprovacao: 'aprovacoes',
    idGrupoAcessoAprovador: 'aprovacoes'
  }
})

// ============================================================
// TOGGLE DO HEADER DE ABAS — não expande ao clicar nas tabs
// ============================================================
const toggleAbasHeader = (event) => {
  if (event.target.closest('.nav-item, .nav-link, button[role="tab"]')) {
    accordionState.abas = true
    return
  }
  accordionState.abas = !accordionState.abas
}

// Bloqueio removido: a desabilitação é feita campo a campo via :disabled
// nas props individuais. A lógica de estado (isReadOnly, estadoBloqueio,
// abasBloqueadas, frameBasicosBloqueado, framesSecundariosBloqueados)
// foi removida — cada campo decide seu próprio :disabled.

const entityInfo = computed(() => ({
  id: item.value.numeroPedidoVenda || item.value.idPedidoVenda,
  name: item.value.numeroPedidoVenda || 'Novo Pedido de Venda',
  nameLabel: 'Pedido de Venda'
}))

const descricaoOperacaoHeader = computed(() => {
  if (operacaoSelecionada.value?.descricao) return operacaoSelecionada.value.descricao
  if (item.value.idOperacao) return `Operação #${item.value.idOperacao}`
  return ''
})

// ============================================================
// MAPA DE SEQUENCIA DOS STATUS (via constantes auto-geradas)
// ============================================================
const SEQUENCIA_FATURADO_PARCIAL = 10

// ============================================================
// VISIBILIDADE DOS BOTÕES DA TOOLBAR
// ============================================================
const statusAtual = computed(() => item.value.lstStatusWorkflowPedido)
// Fallback seguro: `?? 0` (e não `|| 99`) — quando o status do workflow está vazio
// (pedido novo/em digitação sem workflow definido), a sequência deve ser 0 (pré-aprovação,
// editável). O `|| 99` caía para 99 (pós-aprovação) e bloqueava o grid inteiro, mascarando
// o bug de edição do SKU (que antes ignorava o bloqueio).
const sequenciaAtual = computed(() => PED_VEN_WORKFLOW_ST.sequencia?.[statusAtual.value] ?? 0)

const podeCancelar = computed(() => {
  return isPedidoSaved.value &&
    podeEditar.value &&
    item.value.lstStatusPedido !== PED_VEN_ST.Concluido &&
    item.value.lstStatusPedido !== PED_VEN_ST.Cancelado &&
    sequenciaAtual.value < SEQUENCIA_FATURADO_PARCIAL
})

const mostrarConcluir = computed(() => {
  return isPedidoSaved.value &&
    podeEditar.value &&
    item.value.lstStatusPedido !== PED_VEN_ST.Concluido &&
    sequenciaAtual.value >= SEQUENCIA_FATURADO_PARCIAL
})

// A solicitação comercial é exibida conforme a flag de aprovação automática:
// - IsAprovacaoAutomatica == false: sempre aparece (aprovação manual obrigatória)
// - IsAprovacaoAutomatica == true:  só aparece quando há negociação (Criada)
// O botão "Gerar Orçamento" só aparece quando a aprovação automática está ativa
// e o backend sinalizou que não há negociação (Inativa).
const mostrarSolicitarAprovComercial = computed(() => {
  return isPedidoSaved.value &&
    podeEditar.value &&
    statusAtual.value === PED_VEN_WORKFLOW_ST.EmDigitacao &&
    (!isAprovacaoAutomatica.value || item.value.lstStatusAprovacaoComercial === WORK_APR_ST.Criada)
})

const mostrarGerarOrcamento = computed(() => {
  return isPedidoSaved.value &&
    podeEditar.value &&
    statusAtual.value === PED_VEN_WORKFLOW_ST.EmDigitacao &&
    item.value.lstStatusAprovacaoComercial === WORK_APR_ST.Inativa &&
    isAprovacaoAutomatica.value
})

const mostrarFecharOrcamento = computed(() => {
  return isPedidoSaved.value && podeEditar.value && statusAtual.value === PED_VEN_WORKFLOW_ST.AprovadoComercial
})

const mostrarBotaoDocumentoOrcamento = computed(() => {
  // Proforma Invoice (exportação) ou Orçamento de Venda (interno)
  // Disponível a partir de Orçamento, desde que não cancelado
  return isPedidoSaved.value &&
    item.value.lstStatusPedido !== PED_VEN_ST.Cancelado &&
    sequenciaAtual.value >= (PED_VEN_WORKFLOW_ST.sequencia?.[PED_VEN_WORKFLOW_ST.Orcamento] || 99)
})

const isExportacao = computed(() => {
  if (item.value.isExportacao !== undefined && item.value.isExportacao !== null) {
    return item.value.isExportacao
  }
  return !!operacaoSelecionada.value?.cfopExterior
})

const mostrarConfirmarPedido = computed(() => {
  return isPedidoSaved.value && podeEditar.value && statusAtual.value === PED_VEN_WORKFLOW_ST.Orcamento
})

const mostrarEncerrarComSaldo = computed(() => {
  return isPedidoSaved.value && podeEditar.value && statusAtual.value === PED_VEN_WORKFLOW_ST.FaturadoParcial
})

const mostrarEncerrarPedido = computed(() => {
  return isPedidoSaved.value && podeEditar.value &&
    statusAtual.value === PED_VEN_WORKFLOW_ST.ReprovadoComercial &&
    item.value.lstStatusPedido !== PED_VEN_ST.Concluido
})

const mostrarReabrirPedido = computed(() => {
  return isPedidoSaved.value && podeEditar.value &&
    (statusAtual.value === PED_VEN_WORKFLOW_ST.ReprovadoComercial ||
     statusAtual.value === PED_VEN_WORKFLOW_ST.BloqueadoPorCredito ||
     item.value.lstStatusPedido === PED_VEN_ST.Cancelado) &&
    item.value.lstStatusPedido !== PED_VEN_ST.Concluido
})

// ============================================================
// HANDLERS DA TOOLBAR
// ============================================================
// Salva o cabeçalho antes de acionar a transição de status (os itens são gerenciados
// pelo endpoint dedicado /PedidoVendaProdutos).
const salvarCabecalhoAntesDaTransicao = async () => {
  const payload = { ...item.value }
  delete payload.itens
  await api.put(`/PedidoVendas/${item.value.idPedidoVenda}`, payload)
}

// Extrai a mensagem de erro do backend, incluindo a lista de itens sem saldo
// (campo `erros`) devolvida pelo endpoint /confirmar.
const extrairMensagemErro = (e, fallback) => {
  const data = e.response?.data
  let msg = fallback
  if (typeof data === 'string') msg = data
  else if (data?.message) msg = data.message
  else if (data?.title) msg = data.title
  if (Array.isArray(data?.erros) && data.erros.length) {
    msg = `${msg} ${data.erros.join(' ')}`
  }
  return msg
}

// ── Confirmação genérica de transições ──
// Abre o ConfirmationModal com a mensagem da transição pendente.
// O callback onConfirmTransicao executa a ação se confirmada.
const confirmarTransicao = (acao, mensagem) => {
  transicaoPendente.value = { acao, mensagem }
  if (confirmacaoTransicaoRef.value) {
    confirmacaoTransicaoRef.value.show()
  }
}

const onConfirmTransicao = async (confirmed) => {
  if (!confirmed || !transicaoPendente.value) {
    transicaoPendente.value = null
    return
  }
  const { acao } = transicaoPendente.value
  transicaoPendente.value = null
  await acao()
}

const handleConfirmarPedido = async () => {
  if (!podeEditar.value || isLoading.value) return
  isLoading.value = true
  try {
    await salvarCabecalhoAntesDaTransicao()
    await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/confirmar`, {})
    emit('show-notification', { message: 'Pedido confirmado com sucesso!', type: 'success' })
    await carregarCabecalho()
    emit('itemAtualizado', item.value)
    // Os dados já foram persistidos (PUT + POST): re-sincroniza a baseline do formulário
    // para que `hasChanges` volte a false (evita o modal de alterações não salvas ao cancelar).
    initializeForm(item.value)
  } catch (e) {
    const data = e.response?.data
    // Erro estruturado (ValidationErrorResponse): destaca campos, navega para a aba
    // correta e foca no primeiro erro (ex: solicitação Pendente sem aprovador).
    if (Array.isArray(data?.errors) && data.errors.length) {
      processarErrosValidacaoPV(data)
    }
    emit('show-notification', { message: extrairMensagemErro(e, 'Erro ao confirmar o pedido.'), type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleCancelarPedido = async () => {
  if (!podeEditar.value || isLoading.value) return
  isLoading.value = true
  try {
    await salvarCabecalhoAntesDaTransicao()
    await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/cancelar`, {})
    emit('show-notification', { message: 'Pedido cancelado com sucesso!', type: 'success' })
    await carregarCabecalho()
    emit('itemAtualizado', item.value)
    // Os dados já foram persistidos (PUT + POST): re-sincroniza a baseline do formulário
    // para que `hasChanges` volte a false (evita o modal de alterações não salvas ao cancelar).
    initializeForm(item.value)
  } catch (e) {
    emit('show-notification', { message: extrairMensagemErro(e, 'Erro ao cancelar o pedido.'), type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleFecharOrcamento = async () => {
  if (!podeEditar.value || isLoading.value) return
  if (!item.value.idPedidoVenda) return
  isLoading.value = true
  try {
    const res = await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/fechar-orcamento`, {})
    Object.assign(item.value, res.data)
    emit('show-notification', { message: 'Orçamento fechado com sucesso!', type: 'success' })
    emit('itemAtualizado', item.value)
    // Os dados já foram persistidos (POST): re-sincroniza a baseline do formulário
    // para que `hasChanges` volte a false (evita o modal de alterações não salvas ao cancelar).
    initializeForm(item.value)
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao fechar orçamento.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleEncerrarComSaldo = async () => {
  // Endpoint pendente de implementação
  emit('show-notification', { message: 'Botão sem funcionalidade - endpoint será implementado em breve.', type: 'warning' })
}

const handleEncerrarPedido = () => {
  if (!podeEditar.value || isLoading.value) return
  if (confirmacaoEncerrarRef.value) {
    confirmacaoEncerrarRef.value.show()
  }
}

const onConfirmEncerrarPedido = async (confirmed) => {
  if (!confirmed) return
  isLoading.value = true
  try {
    const res = await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/encerrar`, {})
    Object.assign(item.value, res.data)
    await carregarStatusReserva()
    emit('show-notification', { message: 'Pedido encerrado com sucesso!', type: 'success' })
    emit('itemAtualizado', item.value)
    initializeForm(item.value)
  } catch (e) {
    emit('show-notification', { message: extrairMensagemErro(e, 'Erro ao encerrar o pedido.'), type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleReabrirPedido = () => {
  if (!podeEditar.value || isLoading.value) return
  if (confirmacaoReabrirRef.value) {
    confirmacaoReabrirRef.value.show()
  }
}

const onConfirmReabrirPedido = async (confirmed) => {
  if (!confirmed) return
  isLoading.value = true
  try {
    const res = await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/reabrir`, {})
    Object.assign(item.value, res.data)
    await carregarStatusReserva()
    emit('show-notification', { message: 'Pedido reaberto para "Em Digitação". Uma nova solicitação de aprovação comercial foi criada.', type: 'success' })
    initializeForm(item.value)
    if (workflowGridRef.value) workflowGridRef.value.recarregar()
  } catch (e) {
    emit('show-notification', { message: extrairMensagemErro(e, 'Erro ao reabrir o pedido.'), type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleOrcamentoVenda = async () => {
  // Endpoint pendente de implementação
  emit('show-notification', { message: 'Botão sem funcionalidade - endpoint será implementado em breve.', type: 'warning' })
}

const podeEditar = computed(() => {
  if (props.pipelineProps?.readOnly) return false
  if (authStore.isAdminInterno) return true
  if (authStore.pode('PEDIDO_VENDA_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('PEDIDO_VENDA').some(p => p === 'COMPLETO' || p.includes('COMPLETO'))
})

// ============================================================
// ESTADO READ-ONLY + BLOQUEIO CAMPO A CAMPO
// ============================================================
/// Indica se o pedido está em estado de apenas leitura (workflow avançou além
/// de "Em Digitação" ou status terminal). Neste modo, os campos são
/// desabilitados individualmente via `campoDesabilitado`, exceto os campos da
/// lista de exceções (observações, status de item, etc.).
const isReadOnly = computed(() => {
  if (!isPedidoSaved.value) return false
  const workflowAvancou = statusAtual.value &&
    statusAtual.value !== PED_VEN_WORKFLOW_ST.EmDigitacao
  const statusTerminal = item.value.lstStatusPedido === PED_VEN_ST.Cancelado ||
    item.value.lstStatusPedido === PED_VEN_ST.Concluido
  return workflowAvancou || statusTerminal
})

/// Campos que permanecem editáveis mesmo quando o pedido está read-only.
/// Exceções alinhadas às necessidades do negócio: observação do pedido,
/// observação/status do item e futura requisição de aprovação de crédito.
const CAMPOS_SEMPRE_EDITAVEIS = [
  'observacaoInterna',
  'observacao',
  'lstStatusPedidoItem',
  'idGrupoAcessoAprovador'
]

/// Helper GLOBAL de bloqueio por campo/botão.
/// - Sem permissão (RBAC `podeEditar`) → desabilitado.
/// - Pedido read-only (workflow avançou) e campo NÃO exceção → desabilitado.
/// - Regras específicas por campo (ex: operacaoFinanciada bloqueado a partir de
///   AguardandoPagamento seq 9 — o campo não pode mais ser alterado após essa etapa).
/// - Caso contrário → habilitado.
const campoDesabilitado = (campo) => {
  if (!podeEditar.value) return true
  if (isReadOnly.value && !CAMPOS_SEMPRE_EDITAVEIS.includes(campo)) return true
  // Regra: OperacaoFinanciada bloqueada a partir de AguardandoPagamento (seq 9)
  if (campo === 'operacaoFinanciada' && sequenciaAtual.value >= (PED_VEN_WORKFLOW_ST.sequencia?.[PED_VEN_WORKFLOW_ST.AguardandoPagamento] || 99)) return true
  return false
}

/// True quando o formulário está bloqueado por RBAC ou workflow read-only.
/// Usado nos botões de edição (que não são exceções): salvar, adicionar item,
/// gerenciar condições/pricing/comissões. Os botões da TOOLBAR do pedido
/// (workflow) permanecem executáveis e NÃO usam este helper.
const isFormBloqueado = computed(() => !podeEditar.value || isReadOnly.value)

// ============================================================
// RBAC — Modo de Precificação
// ============================================================

// Indica se o usuário pode escolher livremente o modo de precificação.
// AdminInterno/AdminCliente sempre podem; usuários com a permissão
// PEDIDO_VENDA_PRECIFICACAO_COMPLETA também.
const podeEscolherModoPrecificacao = computed(() => {
  if (authStore.isAdminInterno || authStore.isAdminCliente) return true
  return authStore.pode('PEDIDO_VENDA_PRECIFICACAO_COMPLETA')
})

// Quando travado, o campo LstModoPrecificacao fica bloqueado (default seguro).
const modoPrecificacaoTravado = computed(() => !podeEscolherModoPrecificacao.value)

// Indica se o modo ativo é "Precificação por Tabela" (controla a visibilidade da tabela).
const isModoPorTabela = computed(() =>
  item.value.lstModoPrecificacao === PED_VEN_MODO_PRECIFIC.PrecificacaoPorTabela
)

// Configuração de busca das Tabelas de Preços vinculadas ao cliente do pedido.
const configBuscaPrecoComercialCliente = computed(() =>
  criarConfigBuscaPrecoComercialCliente(item.value.idParceiroNegocioCliente)
)

// Indica se o pedido está pós-aprovação (workflow fora de EmDigitação/EmAprovação)
// ou em status terminal (Cancelado/Concluído). Nesses casos, apenas o Câmbio
// permanece editável.
const temModuloPedidoVenda = computed(() => {
  return authStore.temModulo('PEDIDO_VENDA')
})

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: {
    // A busca de Filial filtra por organização (FiliaisController aceita apenas idOrganizacao).
    idOrganizacao: authStore.organizacaoAtiva
  }
}))

const configBuscaCliente = computed(() => ({
  ...parceiroSearch,
  titulo: 'Selecionar Cliente',
  filtrosFixos: {
    isCliente: true
  }
}))

// Configuração de busca para Operação — Vendas
const configBuscaOperacao = computed(() =>
  criarConfigBuscaOperacaoVenda()
)

const configBuscaCondicaoPagamento = computed(() =>
  criarConfigBuscaCondicaoPagamento()
)

const configBuscaIndicador = computed(() => ({
  ...parceiroSearch,
  titulo: 'Selecionar Parceiro Indicador',
  filtrosFixos: {
    lstStatus: ST.Ativo
  }
}))

const configBuscaLocalEstoquePadrao = computed(() => {
  // Nunca retorna null: o ModalDeBusca exige um objeto de configuração
  // (acessa configuracao.titulo / idField / descField diretamente).
  // Quando não há Filial Padrão selecionada, o campo é desabilitado no template.
  // O Local de Estoque Padrão do pedido só aceita estoques FINAIS
  // (ProprioInterno / TerceirosArmazemGeral) — estoques de trânsito são
  // selecionados linha a linha via SelecaoProdutoEstoqueModal.
  return criarConfigBuscaLocalEstoque(
    authStore.organizacaoAtiva,
    idFilialPadrao.value,
    ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM.CodigosEstoqueFinal
  )
})

// Watch: limpa o local de estoque padrão quando a filial padrão mudar
watch(idFilialPadrao, () => {
  idLocalEstoquePadrao.value = null
  descricaoLocalEstoquePadrao.value = ''
})

// Guarda a descrição do local de estoque padrão selecionado, para que novas
// linhas do grid de itens já venham com o local preenchido (id + descrição).
const onLocalEstoquePadraoSelecionado = (local) => {
  descricaoLocalEstoquePadrao.value = local?.descricao || ''
}

// Pré-preenche a Filial Padrão com a filial do pedido (default dos itens),
// garantindo contexto para a busca de Local de Estoque sem exigir seleção manual.
watch(() => item.value.idFilial, (novaFilial) => {
  if (novaFilial && !idFilialPadrao.value) {
    idFilialPadrao.value = novaFilial
  }
}, { immediate: true })

const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const found = lista.find(l => l.idListaClienteItem === id || l.idListaSistemaItem === id)
  return found ? found.textoLista : (id || '-')
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

const formatarMoeda = (valor) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0)
}

const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const idStr = String(codigoMoeda)
  const itemLista = (listas.LstMoeda || []).find(m =>
    String(m.idListaClienteItem) === idStr || String(m.idListaClienteItem) === idStr
  )
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

// Labels dinâmicas de taxa (moeda do pedido / moeda padrão / moeda gerencial)
const labelTaxaPadrao = computed(() => {
  const siglaPedido = obterSiglaMoeda(item.value.lstMoeda)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaPedido}/${siglaPadrao}`
})

const labelTaxaGerencialPadrao = computed(() => {
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaGerencial}/${siglaPadrao}`
})

const labelTaxaCambioGerencial = computed(() => {
  const siglaPedido = obterSiglaMoeda(item.value.lstMoeda)
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  return `Taxa ${siglaPedido}/${siglaGerencial}`
})

// ============================================================
// CÂMBIO — Resolução Automática
// ============================================================
const isResolvendoTaxas = ref(false)

const resolverTaxasAutomaticamente = async () => {
  if (!item.value.idPedidoVenda || isResolvendoTaxas.value) return
  isResolvendoTaxas.value = true
  try {
    const res = await api.post(`/PedidoVendas/${item.value.idPedidoVenda}/resolver-taxas`)
    Object.assign(item.value, res.data)
    // Limpar preview para forçar fallback aos valores persistidos
    dadosCambioPreview.value = null
    emit('show-notification', { message: 'Taxas de câmbio resolvidas automaticamente.', type: 'success' })
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao resolver taxas de câmbio.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isResolvendoTaxas.value = false
  }
}

// ============================================================
// CÂMBIO — Modal de Cotação Manual
// ============================================================
const isModalCotacaoManualOpen = ref(false)

const cotacaoManualData = reactive({
  taxaCambioPadrao: null,
  taxaCambioGerencialPadrao: null,
  taxaCambioGerencial: null
})

watch(
  () => [cotacaoManualData.taxaCambioPadrao, cotacaoManualData.taxaCambioGerencialPadrao],
  ([taxaPadrao, taxaGerencialPadrao]) => {
    if (taxaPadrao && taxaGerencialPadrao && Number(taxaGerencialPadrao) !== 0) {
      cotacaoManualData.taxaCambioGerencial = Math.round(
        (Number(taxaPadrao) / Number(taxaGerencialPadrao)) * 1e6
      ) / 1e6
    }
  }
)

const abrirModalCotacaoManual = () => {
  cotacaoManualData.taxaCambioPadrao = item.value.taxaCambioPadrao
  cotacaoManualData.taxaCambioGerencialPadrao = item.value.taxaCambioGerencialPadrao
  cotacaoManualData.taxaCambioGerencial = item.value.taxaCambioGerencial
  isModalCotacaoManualOpen.value = true
}

const salvarCotacaoManual = async () => {
  item.value.taxaCambioPadrao = cotacaoManualData.taxaCambioPadrao
  item.value.taxaCambioGerencialPadrao = cotacaoManualData.taxaCambioGerencialPadrao
  item.value.taxaCambioGerencial = cotacaoManualData.taxaCambioGerencial
  item.value.lstFonteCambio = MOE_FONTE_COTACAO.Manual
  item.value.lstTaxaCambioStatus = FIN_ST_TAX_CONVERCAO.Definitiva

  // Usar MULTIPLICAÇÃO (consistente com ConversaoMonetariaService)
  const taxaPadrao = Number(item.value.taxaCambioPadrao) || 0
  const taxaGerencial = Number(item.value.taxaCambioGerencial) || 0

  if (taxaPadrao > 0) {
    item.value.valorTotalOriginalPadrao = Math.round(item.value.valorTotalOriginal * taxaPadrao * 100) / 100
    item.value.valorTotalFretePadrao = Math.round(item.value.valorTotalFrete * taxaPadrao * 100) / 100
    item.value.valorTotalDescontoPadrao = Math.round(item.value.valorTotalDesconto * taxaPadrao * 100) / 100
    item.value.valorTotalNegociadoPadrao = Math.round(item.value.valorTotalNegociado * taxaPadrao * 100) / 100
  }
  if (taxaGerencial > 0) {
    item.value.valorTotalOriginalGerencial = Math.round(item.value.valorTotalOriginal * taxaGerencial * 100) / 100
    item.value.valorTotalFreteGerencial = Math.round(item.value.valorTotalFrete * taxaGerencial * 100) / 100
    item.value.valorTotalDescontoGerencial = Math.round(item.value.valorTotalDesconto * taxaGerencial * 100) / 100
    item.value.valorTotalNegociadoGerencial = Math.round(item.value.valorTotalNegociado * taxaGerencial * 100) / 100
  }

  if (item.value.idPedidoVenda) {
    try {
      const payload = { ...item.value }
      await api.put(`/PedidoVendas/${item.value.idPedidoVenda}`, payload)
      // Recarrega os dados atualizados do backend
      const res = await api.get(`/PedidoVendas/${item.value.idPedidoVenda}`)
      Object.assign(item.value, res.data)
      dadosCambioPreview.value = null
      emit('show-notification', { message: 'Taxas de câmbio atualizadas com sucesso.', type: 'success' })
      // Os dados já foram persistidos (PUT): re-sincroniza a baseline do formulário
      // para que `hasChanges` volte a false (evita o modal de alterações não salvas ao cancelar).
      initializeForm(item.value)
    } catch (error) {
      const data = error.response?.data
      let msg = 'Erro ao salvar taxas de câmbio.'
      if (typeof data === 'string') msg = data
      else if (data?.message) msg = data.message
      else if (data?.title) msg = data.title
      else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
      emit('show-notification', { message: msg, type: 'error' })
    }
  }

  isModalCotacaoManualOpen.value = false
}

// ============================================================
// CÂMBIO — Preview
// ============================================================
const dadosCambioPreview = ref(null)

const obterCorStatus = (idStatus) => {
  const texto = String(getTextoLista(listas.LstStatusPedido, idStatus)).toUpperCase()
  if (texto.includes('CONCLUÍDO') || texto.includes('FINALIZADO') || texto.includes('FATURADO')) return 'badge-ghost-success'
  if (texto.includes('ABERTO') || texto.includes('ORÇAMENTO') || texto.includes('DIGITAÇÃO')) return 'badge-ghost-warning'
  if (texto.includes('CANCELADO')) return 'badge-ghost-danger'
  if (texto.includes('CONFIRMADO')) return 'badge-ghost-info'
  return 'badge-ghost-secondary'
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas([
      'PedidoVenda',
      'PedidoVendaProduto',
      'Produto',
      'PedidoVendaComissao',
      'PedidoVendaCondicaoPg'
    ])
    Object.assign(listas, result)
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares:', e)
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares.', type: 'error' })
  }
}

const carregarItensPedido = async () => {
  if (!item.value.idPedidoVenda) {
    itensPedido.value = []
    return
  }
  try {
    const res = await api.get('/PedidoVendaProdutos', {
      params: { idPedidoVenda: item.value.idPedidoVenda, pageSize: 999 }
    })
    itensPedido.value = res.data || []
  } catch (e) {
    console.error('Erro ao carregar itens do pedido:', e)
    itensPedido.value = []
  }
}

const carregarCabecalho = async () => {
  if (!item.value.idPedidoVenda) return
  try {
    const res = await api.get(`/PedidoVendas/${item.value.idPedidoVenda}`)
    item.value.valorTotalOriginal = res.data.valorTotalOriginal ?? 0
    item.value.valorTotalDesconto = res.data.valorTotalDesconto ?? 0
    item.value.valorTotalNegociado = res.data.valorTotalNegociado ?? 0
    item.value.valorTotalFrete = res.data.valorTotalFrete ?? 0
    item.value.margemEsperadaPercentual = res.data.margemEsperadaPercentual ?? null
    // Atualiza o status da aprovação comercial (pode ter mudado após ReavaliarNecessidadeAsync)
    if (res.data?.lstStatusAprovacaoComercial !== undefined) {
      item.value.lstStatusAprovacaoComercial = res.data.lstStatusAprovacaoComercial
    }
    // Reflete o estado da reserva de estoque do documento (por-documento).
    await carregarStatusReserva()
  } catch (e) {
    console.error('[carregarCabecalho] Erro:', e)
  }
}

// Status da reserva de estoque do documento (GET /api/ReservasEstoque/por-documento).
// O PV NÃO orquestra a reserva — apenas reflete o estado para exibição no grid/modal de itens.
// 404 (documento sem reserva) é tratado como null (estado de exibição = "sem reserva").
const statusReservaDocumento = ref(null)

const carregarStatusReserva = async () => {
  const idPedido = item.value.idPedidoVenda
  if (!idPedido) {
    statusReservaDocumento.value = null
    return
  }
  try {
    const res = await api.get('/ReservasEstoque/por-documento', {
      params: { tipo: ESTQ_RESERVA_ORIGEM.PedidoVenda, id: idPedido },
      _suppressLoading: true
    })
    statusReservaDocumento.value = res.data || null
  } catch (e) {
    if (e.response?.status === 404) {
      statusReservaDocumento.value = null
    } else {
      console.warn('Erro ao carregar status da reserva:', e)
      statusReservaDocumento.value = null
    }
  }
}

// Handlers para o componente PedidoVendaItensExcel
const handleSalvarItem = async (dadosItem) => {
  if (!isPedidoSaved.value || isLoadingAcao.value) return
  isLoadingAcao.value = true
  try {
    const payload = {
      idPedidoVenda: item.value.idPedidoVenda,
      idProduto: dadosItem.idProduto,
      // Itens sem estoque (ex.: serviços) podem não ter local — envia 0 (não informado).
      idLocalEstoque: dadosItem.idLocalEstoque || 0,
      lstUnidadeMedidaVenda: dadosItem.lstUnidadeMedidaVenda,
      lstModoCusteio: dadosItem.lstModoCusteio || null,
      custoUnitarioSnapshot: dadosItem.custoUnitarioSnapshot != null
        ? Number(dadosItem.custoUnitarioSnapshot)
        : null,
      quantidade: Number(dadosItem.quantidade) || 0,
      valorUnitarioOriginal: Number(dadosItem.valorUnitarioOriginal) || 0,
      valorTotalDesconto: Number(dadosItem.valorTotalDesconto) || 0,
      // MargemEsperadaPercentual e MargemEsperadaOrigem são de responsabilidade
      // exclusiva do backend (calculados conforme modo de precificação).
      observacao: dadosItem.observacao || '',
      percentualTolerancia: dadosItem.percentualTolerancia != null
        ? Number(dadosItem.percentualTolerancia)
        : null,
      descricaoProduto: dadosItem.descricaoProduto || '',
      dataPrevisaoEntrega: dadosItem.dataPrevisaoEntrega || null,
      // Âncoras de origem (1:1) capturadas no modal de seleção de produto/estoque.
      idEmbarque: dadosItem.idEmbarque ?? null,
      idEmbarqueContainer: dadosItem.idEmbarqueContainer ?? null,
      idEmbarquePackingListProduto: dadosItem.idEmbarquePackingListProduto ?? null,
      idLoteFabricacao: dadosItem.idLoteFabricacao ?? null,
      // Origem comercial (faturamento/produtor) — âncora semântica da reserva
      idDocumentoEntrada: dadosItem.idDocumentoEntrada ?? null,
      idDocumentoEntradaItem: dadosItem.idDocumentoEntradaItem ?? null
    }

    if (dadosItem.idPedidoVendaProduto) {
      payload.idPedidoVendaProduto = dadosItem.idPedidoVendaProduto
      payload.xmin = dadosItem.xmin
      await api.put(`/PedidoVendaProdutos/${dadosItem.idPedidoVendaProduto}`, payload)
    } else {
      await api.post('/PedidoVendaProdutos', payload)
    }
    await carregarItensPedido()
    // A linha nova salva já existe no servidor: descarta a cópia local pendente
    // (sem id) para não duplicá-la na grade.
    if (!dadosItem.idPedidoVendaProduto && dadosItem._tempId) {
      itensExcelRef.value?.descartarPendente(dadosItem._tempId)
    }
    await carregarCabecalho()
    // A necessidade da aprovação comercial pode ter mudado (desconto/custo simulado).
    if (workflowGridRef.value) workflowGridRef.value.recarregar()
    emit('show-notification', {
      message: dadosItem.idPedidoVendaProduto
        ? 'Item atualizado com sucesso.'
        : 'Item adicionado com sucesso.',
      type: 'success'
    })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar item.'
    if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    emit('show-notification', { message: msg, type: 'error' })
    await carregarItensPedido()
  } finally {
    isLoadingAcao.value = false
  }
}

// Persiste em lote as linhas recalculadas pela cascata de precificação.
// Usa um único guard de loading para não bloquear os itens em sequência.
const handleSalvarTodosItens = async (itens) => {
  if (!isPedidoSaved.value || isLoadingAcao.value) return
  if (!Array.isArray(itens) || itens.length === 0) return
  isLoadingAcao.value = true
  try {
    for (const dadosItem of itens) {
      const payload = {
        idPedidoVenda: item.value.idPedidoVenda,
        idPedidoVendaProduto: dadosItem.idPedidoVendaProduto,
        idProduto: dadosItem.idProduto,
        // Itens sem estoque (ex.: serviços) podem não ter local — envia 0 (não informado).
        idLocalEstoque: dadosItem.idLocalEstoque || 0,
        lstUnidadeMedidaVenda: dadosItem.lstUnidadeMedidaVenda,
        lstModoCusteio: dadosItem.lstModoCusteio || null,
        custoUnitarioSnapshot: dadosItem.custoUnitarioSnapshot != null
          ? Number(dadosItem.custoUnitarioSnapshot)
          : null,
        quantidade: Number(dadosItem.quantidade) || 0,
        valorUnitarioOriginal: Number(dadosItem.valorUnitarioOriginal) || 0,
        valorTotalDesconto: Number(dadosItem.valorTotalDesconto) || 0,
        // MargemEsperadaPercentual e MargemEsperadaOrigem são de responsabilidade
        // exclusiva do backend (calculados conforme modo de precificação).
        observacao: dadosItem.observacao || '',
        percentualTolerancia: dadosItem.percentualTolerancia != null
          ? Number(dadosItem.percentualTolerancia)
          : null,
        descricaoProduto: dadosItem.descricaoProduto || '',
        dataPrevisaoEntrega: dadosItem.dataPrevisaoEntrega || null,
        // Âncoras de origem (1:1) capturadas no modal de seleção de produto/estoque.
        idEmbarque: dadosItem.idEmbarque ?? null,
        idEmbarqueContainer: dadosItem.idEmbarqueContainer ?? null,
        idEmbarquePackingListProduto: dadosItem.idEmbarquePackingListProduto ?? null,
        idLoteFabricacao: dadosItem.idLoteFabricacao ?? null,
        // Origem comercial (faturamento/produtor) — âncora semântica da reserva
        idDocumentoEntrada: dadosItem.idDocumentoEntrada ?? null,
        idDocumentoEntradaItem: dadosItem.idDocumentoEntradaItem ?? null,
        xmin: dadosItem.xmin
      }
      await api.put(`/PedidoVendaProdutos/${dadosItem.idPedidoVendaProduto}`, payload)
    }
    await carregarItensPedido()
    await carregarCabecalho()
    // A necessidade da aprovação comercial pode ter mudado (desconto/custo simulado).
    if (workflowGridRef.value) workflowGridRef.value.recarregar()
    emit('show-notification', { message: 'Preços recalculados com sucesso.', type: 'success' })
  } catch (error) {
    const status = error.response?.status
    const data = error.response?.data
    let msg = 'Erro ao recalcular os preços.'
    if (status === 409) {
      msg = 'Um item foi alterado por outro usuário. Recarregue os dados e tente novamente.'
    } else if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
    await carregarItensPedido()
  } finally {
    isLoadingAcao.value = false
  }
}

const handleRemoverItem = async (idPedidoVendaProduto) => {
  if (isLoadingAcao.value) return
  isLoadingAcao.value = true
  try {
    await api.delete(`/PedidoVendaProdutos/${idPedidoVendaProduto}`)
    emit('show-notification', { message: 'Item removido.', type: 'success' })
    await carregarItensPedido()
    await carregarCabecalho()
    // A necessidade da aprovação comercial pode ter mudado (desconto/custo simulado).
    if (workflowGridRef.value) workflowGridRef.value.recarregar()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao remover item.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

// Handler do cancelamento de item (endpoint POST /PedidoVendaProdutos/{id}/cancelar
// executado DENTRO do PedidoVendaItensExcel). O grid já aplica o status Cancelado
// na linha imediatamente; aqui apenas re-sincronizamos itens, cabeçalho (totais/
// margem/status), status da reserva e grid de aprovações — o cancelamento pode ter
// alterado a necessidade de aprovação comercial ou retornado o pedido à digitação.
// O id do item não é necessário nas recargas (trazem o estado completo do pedido).
const handleCancelarItem = async (_idPedidoVendaProduto) => {
  await carregarItensPedido()
  await carregarCabecalho()
  await carregarStatusReserva()
  // A necessidade da aprovação comercial pode ter mudado (margem abaixo da aprovada).
  if (workflowGridRef.value) workflowGridRef.value.recarregar()
}

const carregarCondicoesPagamento = async () => {
  if (!item.value.idPedidoVenda) {
    condicoesPagamento.value = []
    return
  }
  isLoadingCondicoes.value = true
  try {
    const res = await api.get('/PedidoVendaCondicoesPg', {
      params: { IdPedidoVenda: item.value.idPedidoVenda }
    })
    condicoesPagamento.value = res.data
  } catch (e) {
    console.error('Erro ao carregar condições de pagamento:', e)
  } finally {
    isLoadingCondicoes.value = false
  }
}

const iniciarNovaCondicao = () => {
  condicaoEmEdicao.value = {
    idPedidoVendaCondicaoPg: 0,
    idPedidoVenda: item.value.idPedidoVenda,
    percentualDoTotal: null,
    descricao: '',
    lstReferenciaTemporal: '',
    lstCondPgMomento: 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: 'COND_PG_TIPO_CALCULO1',
    prazoDias: null
  }
  showModalCondicao.value = true
}

const editarCondicao = (cond) => {
  condicaoEmEdicao.value = { ...cond }
  showModalCondicao.value = true
}

const salvarCondicao = async () => {
  const c = { ...condicaoEmEdicao.value }
  if (!c.lstReferenciaTemporal || c.percentualDoTotal === null) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios da condição de pagamento.', type: 'warning' })
    return
  }
  const payload = {
    idPedidoVenda: item.value.idPedidoVenda,
    percentualDoTotal: Number(String(c.percentualDoTotal).replace(',', '.')) || 0,
    descricao: c.descricao || '',
    lstReferenciaTemporal: c.lstReferenciaTemporal,
    lstCondPgMomento: c.lstCondPgMomento || 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: c.lstCondPgUnidadeTempo || 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: c.lstCondPgTipoCalculo || 'COND_PG_TIPO_CALCULO1',
    prazoDias: Math.abs(Number(c.prazoDias) || 0)
  }
  try {
    if (c.idPedidoVendaCondicaoPg) {
      await api.put(`/PedidoVendaCondicoesPg/${c.idPedidoVendaCondicaoPg}`, payload)
    } else {
      await api.post('/PedidoVendaCondicoesPg', payload)
    }
    emit('show-notification', { message: 'Condição de pagamento salva com sucesso.', type: 'success' })
    condicaoEmEdicao.value = null
    showModalCondicao.value = false
    await carregarCondicoesPagamento()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao salvar condição de pagamento.', type: 'error' })
  }
}

const solicitarExclusaoCondicao = (cond) => {
  modalExclusaoCondicao.value.id = cond.idPedidoVendaCondicaoPg
  if (modalExclusaoCondicaoRef.value) {
    modalExclusaoCondicaoRef.value.show()
  }
}

const confirmarExclusaoCondicao = async (confirmed) => {
  if (!confirmed) return
  try {
    await api.delete(`/PedidoVendaCondicoesPg/${modalExclusaoCondicao.value.id}`)
    emit('show-notification', { message: 'Condição de pagamento removida com sucesso.', type: 'success' })
    await carregarCondicoesPagamento()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao remover condição de pagamento.', type: 'error' })
  }
}

const gerarDescricaoCondicao = (cond) => {
  if (!cond) return ''
  const prazo = Math.abs(Number(cond.prazoDias) || 0)
  const refTemporal = getTextoLista(listas.LstReferenciaTemporal, cond.lstReferenciaTemporal)
  const percentual = cond.percentualDoTotal || 0
  const momento = getTextoLista(listas.LstCondPgMomento, cond.lstCondPgMomento)
  const unidade = getTextoLista(listas.LstCondPgUnidadeTempo, cond.lstCondPgUnidadeTempo)
  const tipoCalc = getTextoLista(listas.LstCondPgTipoCalculo, cond.lstCondPgTipoCalculo)
  return `${percentual}${tipoCalc} ${prazo} ${unidade} ${momento} ${refTemporal}`
}

const validarRegrasDeNegocio = async () => {
  const labelsObrigatorios = document.querySelectorAll('label.is-required')
  let hasBaseErrors = false

  for (const key in errors.value) {
    delete errors.value[key]
  }

  labelsObrigatorios.forEach(label => {
    if (label.offsetParent === null) return
    const inputId = label.getAttribute('for')
    if (!inputId) return
    const inputElement = document.getElementById(inputId)
    if (!inputElement) return

    let isEmpty = false
    if (inputElement.tagName === 'SELECT') {
      isEmpty = !inputElement.value || inputElement.value === ''
    } else {
      isEmpty = !inputElement.value || String(inputElement.value).trim() === ''
    }

    if (isEmpty) {
      const nomeCampoLimpo = label.innerText.replace('*', '').trim()
      errors.value[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
      hasBaseErrors = true
    }
  })

  if (hasBaseErrors) {
    console.warn('[DEBUG-solicitarAprov] validarRegrasDeNegocio: hasBaseErrors = TRUE. Campos vazios detectados:', Object.keys(errors.value))
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.', type: 'warning' })
    if (focarPrimeiroErro) await focarPrimeiroErro()
    return false
  }

  console.log('[DEBUG-solicitarAprov] validarRegrasDeNegocio: OK, sem campos obrigatorios vazios visiveis.')
  return true
}

const salvar = async () => {
  if (!podeEditar.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = { ...item.value }

  // Os itens são gerenciados pelo endpoint dedicado /PedidoVendaProdutos.
  // Enviar 'itens' aqui dispara a substituição (RemoveRange + Add) no backend,
  // invalidando os IDs/xmin dos itens (cascade save proibido).
  delete payload.itens

  // idEmpresa é sempre preenchido automaticamente pela sessão do usuário

  if (!payload.idPedidoVenda) {
    payload.lstStatusPedido = PED_VEN_ST.EmAndamento
    payload.lstStatusWorkflowPedido = PED_VEN_WORKFLOW_ST.EmDigitacao
  }

  try {
    isLoading.value = true
    const res = await api[payload.idPedidoVenda ? 'put' : 'post'](
      payload.idPedidoVenda ? `/PedidoVendas/${payload.idPedidoVenda}` : '/PedidoVendas',
      payload
    )

    initializeForm(res.data)
    isPedidoSaved.value = true

    emit('show-notification', { message: 'Registro salvo com sucesso!', type: 'success' })
    emit('itemAtualizado', res.data)
  } catch (e) {
    console.error('Erro ao salvar:', e)
    const data = e.response?.data
    let msg = 'Erro ao salvar o registro.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const salvarAlteracoes = async () => {
  if (!podeEditar.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = { ...item.value }

  // Os itens são gerenciados pelo endpoint dedicado /PedidoVendaProdutos.
  // Enviar 'itens' aqui dispara a substituição (RemoveRange + Add) no backend,
  // invalidando os IDs/xmin dos itens (cascade save proibido).
  delete payload.itens

  try {
    isLoading.value = true
    const res = await api.put(`/PedidoVendas/${item.value.idPedidoVenda}`, payload)

    initializeForm(res.data)

    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (e) {
    console.error('Erro ao salvar alterações:', e)
    const data = e.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleCancel = () => {
  const action = () => {
    if (props.drilldownMode) {
      emit('drilldown-pop')
    } else {
      emit('cancelar')
    }
  }
  if (hasChanges.value) {
    pendingAction.value = action
    confirmationModalRef.value.show()
  } else {
    action()
  }
}

const onConfirmTransition = (confirmed) => {
  if (confirmed && pendingAction.value) {
    pendingAction.value()
  }
  pendingAction.value = null
}

const handleProformaInvoice = async () => {
  if (!isPedidoSaved.value) return
  try {
    const res = await api.get(`/PedidoVendas/${item.value.idPedidoVenda}/proforma-invoice`, {
      responseType: 'blob'
    })
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `ProformaInvoice_${item.value.numeroPedidoVenda || item.value.idPedidoVenda}.pdf`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    emit('show-notification', { message: 'Proforma Invoice gerada com sucesso!', type: 'success' })
  } catch (e) {
    console.error('Erro ao gerar Proforma Invoice:', e)
    emit('show-notification', { message: 'Erro ao gerar Proforma Invoice.', type: 'error' })
  }
}

onMounted(async () => {
  await carregarListas()

  if (!item.value.idPedidoVenda) {
    initializeForm(item.value)
  }

  // Inicializa toggle de operação triangular
  operacaoTriangular.value = !!item.value.idParceiroRemessa

  if (item.value.idPedidoVenda) {
    await carregarItensPedido()
    await carregarCondicoesPagamento()
    await carregarStatusReserva()
    sincronizarJsonParaValores()
  }

  // Se veio do contrato, carrega dados
  if (idContratoEfetivo.value) {
    await carregarPedidosDoContrato()
  }

  // Se está no modo espera, accordions ficam abertos
  if (modoEspera.value) {
    // accordions ficam abertos
  }

  // Carrega comissões se já houver pedido salvo (apenas leitura, sem gerar)
  if (item.value.idPedidoVenda) {
    await carregarComissoes()
  }

  // Carrega a flag de aprovação automática da empresa (Master)
  await carregarIsAprovacaoAutomatica()
})

watch(() => props.itemParaEditar, async (newVal) => {
  if (newVal) {
    item.value = { ...newVal }
    operacaoTriangular.value = !!item.value.idParceiroRemessa
    if (item.value.idPedidoVenda) {
      await carregarItensPedido()
      await carregarCondicoesPagamento()
      await carregarStatusReserva()
      sincronizarJsonParaValores()
    }
  }
}, { deep: true })

watch(() => item.value.idOperacao, async (newId) => {
  if (newId && !operacaoSelecionada.value) {
    try {
      const res = await api.get(`/Operacoes/${newId}`)
      operacaoSelecionada.value = res.data
    } catch (e) {
      console.warn('Erro ao carregar operação para determinar regime:', e)
    }
  }
}, { immediate: true })

// ============================================================
// WATCH: Recarregar cláusulas quando cliente ou itens mudarem
// ============================================================
watch([() => item.value.idParceiroNegocioCliente, () => itensPedido.value], async ([, itens]) => {
  if (!isPedidoSaved.value || !itens?.length) return

  const idsAtuais = itens
    .map(i => i.idProduto)
    .filter(id => id != null && id !== undefined)
  if (idsAtuais.length === 0) return

  if (clausulasGridRef.value) {
    await clausulasGridRef.value.recarregar()
  }
})

// ============================================================
// COMISSÕES — Definidor de Parceiros Recebedores de Comissão
// ============================================================
const comissoes = ref([])
const comissaoKeyCounter = ref(0)
const isLoadingComissao = ref(false)
const isLoadingGestores = ref(false)

// Fila serializadora das operações de comissões (/gerar, /recalcular, /gerar-gestores).
// Enquanto uma operação está em andamento, as demais aguardam na fila — nunca disparam
// POSTs concorrentes. Sem isso, duas requisições simultâneas removem os mesmos gestores
// com xmin divergente e o backend lança DbUpdateConcurrencyException.
let filaComissoes = Promise.resolve()

const enfileirarComissoes = (operacao) => {
  const proxima = filaComissoes.then(operacao, operacao)
  filaComissoes = proxima.catch(() => {})
  return proxima
}

// --- Separa gestores das demais comissões ---
const gestores = computed(() =>
  comissoes.value.filter(c => c.lstPapelComercial === PARCEIRO_VND_PAPEL_COM.GestorComercial)
)

const comissoesSemGestor = computed(() =>
  comissoes.value.filter(c => c.lstPapelComercial !== PARCEIRO_VND_PAPEL_COM.GestorComercial)
)

// --- Verifica se há vendedores definidos nas comissões ---
const temVendedoresDefinidos = computed(() =>
  comissoesSemGestor.value.some(c => c.idParceiroVendedor && c.idParceiroVendedor > 0)
)

// --- Modo de comissão conforme a modalidade ---
const modoComissao = computed(() => {
  const modalidade = item.value.lstModalidadeVenda
  if (modalidade === PED_VEN_MODALIDADE_VND.VendaCompartilhadaSplit) return 'split'
  if (modalidade === PED_VEN_MODALIDADE_VND.EstruturadaSDRCloser) return 'sdr-closer'
  if (modalidade === PED_VEN_MODALIDADE_VND.VendaDiretaComum) return 'venda-direta'
  if (modalidade === PED_VEN_MODALIDADE_VND.VendaIndiretaCanalBroker) return 'broker'
  if (modalidade === PED_VEN_MODALIDADE_VND.VendaRepresentada) return 'representada'
  if (modalidade === PED_VEN_MODALIDADE_VND.Autoatendimento) return 'autoatendimento'
  return 'none'
})

// Regras derivadas diretamente da constante da lista estrutural (sem strings mágicas)
const podeAdicionarComissao = computed(() =>
  item.value.lstModalidadeVenda === PED_VEN_MODALIDADE_VND.VendaCompartilhadaSplit
)
const mostrarRateio = computed(() =>
  item.value.lstModalidadeVenda === PED_VEN_MODALIDADE_VND.VendaCompartilhadaSplit
)

// --- Totalizador de rateio (Split) ---
const totalRateio = computed(() => {
  if (modoComissao.value !== 'split') return 0
  return comissoes.value.reduce((sum, c) => sum + (Number(c.percentualRateio) || 0), 0)
})

const classeTotalizador = computed(() => {
  const total = totalRateio.value
  if (Math.abs(total - 100) < 0.01) return 'badge-ghost-success'
  if (total < 100) return 'badge-ghost-warning'
  return 'badge-ghost-danger'
})

// Executor único das operações de comissões: aplica o resultado na lista reativa,
// gerencia flags de loading e notificações. Sempre chamado via enfileirarComissoes().
const executarOperacaoComissoes = async (endpoint, silent, mensagemSucesso, loadingRef) => {
  isLoadingGestores.value = true
  if (loadingRef) loadingRef.value = true
  try {
    const res = await api.post(endpoint)
    comissoes.value = (res.data || []).map(c => ({
      ...c,
      _key: `com-${c.idPedidoVendaComissao || comissaoKeyCounter.value++}`
    }))
    if (!silent) {
      emit('show-notification', { message: mensagemSucesso, type: 'success' })
    }
  } catch (e) {
    const data = e.response?.data
    const msg = data?.message || 'Erro ao processar comissões.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    if (loadingRef) loadingRef.value = false
    isLoadingGestores.value = false
  }
}

// --- Geração automática de comissões ---
const gerarComissoes = () => {
  const idPedido = item.value.idPedidoVenda
  if (!idPedido || isLoadingComissao.value) return Promise.resolve()
  return enfileirarComissoes(() => executarOperacaoComissoes(
    `/PedidoVendaComissoes/gerar/${idPedido}`,
    false,
    'Comissões geradas automaticamente.',
    isLoadingComissao
  ))
}

// --- Orquestrador: recalcula percentuais, gestores e valorProjetado ---
// `silent = true` suprime a notificação de êxito (usado quando o recálculo é
// um efeito colateral automático, ex: mudança de valor total do pedido).
const recalcularComissoes = (silent = false) => {
  const idPedido = item.value.idPedidoVenda
  if (!idPedido) return Promise.resolve()
  return enfileirarComissoes(() => executarOperacaoComissoes(
    `/PedidoVendaComissoes/recalcular/${idPedido}`,
    silent,
    'Comissões e gestores recalculados!'
  ))
}

// --- Geração de gestores em cascata (mantido para compatibilidade, mas orquestrador é o recomendado) ---
const gerarGestores = () => {
  const idPedido = item.value.idPedidoVenda
  if (!idPedido) return Promise.resolve()
  return enfileirarComissoes(() => executarOperacaoComissoes(
    `/PedidoVendaComissoes/gerar-gestores/${idPedido}`,
    false,
    'Gestores atualizados com sucesso!'
  ))
}

// --- Watch: quando a modalidade muda, persiste imediatamente (se o pedido já existe) e regenera comissões ---
watch(() => item.value.lstModalidadeVenda, async (novaModalidade, antigaModalidade) => {
  if (!item.value.idPedidoVenda || !novaModalidade) return
  if (novaModalidade === antigaModalidade) return

  // Ignora recargas programáticas do formulário (carga inicial ou troca de item):
  // só persiste quando houve alteração real feita pelo usuário.
  if (!hasChanges.value) return
  if (!podeEditar.value || isLoading.value) return

  // Persiste o pedido com a nova modalidade (salvar e continuar)
  await salvarAlteracoes()

  await gerarComissoes()
})

// ============================================================
// Força o modo "Por Tabela" quando o usuário não tem permissão
// ============================================================
// O flag evita que o orquestrador dispare um salvamento programático.
let forcandoModoPrecificacao = false
watch(() => item.value.lstModoPrecificacao, (novo) => {
  if (!modoPrecificacaoTravado.value) return
  if (novo === PED_VEN_MODO_PRECIFIC.PrecificacaoPorTabela) return
  forcandoModoPrecificacao = true
  item.value.lstModoPrecificacao = PED_VEN_MODO_PRECIFIC.PrecificacaoPorTabela
  initializeForm(item.value)
  nextTick(() => { forcandoModoPrecificacao = false })
})

// ============================================================
// Orquestradores de cascata — Modo de Precificação
// ============================================================
const itensExcelRef = ref(null)

// Orquestrador 1 — alteração do modo de precificação:
// persiste o cabeçalho imediatamente e recalcula os preços no grid.
watch(() => item.value.lstModoPrecificacao, async (novoModo, modoAnterior) => {
  if (forcandoModoPrecificacao) return
  if (!isPedidoSaved.value || !novoModo || novoModo === modoAnterior) return
  if (!hasChanges.value || !podeEditar.value || isLoading.value) return

  await salvarAlteracoes()
  // Recarrega os itens para adotar o xmin atualizado pelo save do cabeçalho
  // (evita 409 de concorrência ao recalcular os preços em cascata).
  await carregarItensPedido()
  await nextTick()
  await itensExcelRef.value?.aplicarModoPrecificacao(novoModo, item.value.idPrecoComercial)
})

// Orquestrador 2 — alteração da tabela de preços:
// persiste o cabeçalho imediatamente e recalcula os preços no grid.
watch(() => item.value.idPrecoComercial, async (novoId, idAnterior) => {
  if (!isPedidoSaved.value || novoId === idAnterior) return
  if (!hasChanges.value || !podeEditar.value || isLoading.value) return

  await salvarAlteracoes()
  // Recarrega os itens para adotar o xmin atualizado pelo save do cabeçalho
  // (evita 409 de concorrência ao recalcular os preços em cascata).
  await carregarItensPedido()
  await nextTick()
  await itensExcelRef.value?.aplicarModoPrecificacao(item.value.lstModoPrecificacao, novoId)
})

const carregarComissoes = async () => {
  if (!item.value.idPedidoVenda) {
    comissoes.value = []
    return
  }
  try {
    const res = await api.get('/PedidoVendaComissoes/por-pedido/' + item.value.idPedidoVenda)
    comissoes.value = (res.data || []).map(c => ({ ...c, _key: `com-${c.idPedidoVendaComissao || comissaoKeyCounter.value++}` }))
  } catch (e) {
    console.error('Erro ao carregar comissões:', e)
    comissoes.value = []
  }
}

const adicionarComissao = async () => {
  if (!item.value.idPedidoVenda || !podeEditar.value) return
  try {
    const payload = {
      idParceiroVendedor: null,
      lstPapelComercial: PARCEIRO_VND_PAPEL_COM.VendedorInternoInsideSalesCloser,
      percentualAplicado: 0,
      percentualRateio: null,
      valorProjetado: 0
    }
    const response = await api.post(`/PedidoVendaComissoes/${item.value.idPedidoVenda}`, payload)
    const key = `com-novo-${comissaoKeyCounter.value++}`
    comissoes.value.push({ ...response.data, _key: key })
    emit('show-notification', { message: 'Nova comissão adicionada. Selecione o vendedor.', type: 'info' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao adicionar comissão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const removerComissao = async (comissao) => {
  if (!podeEditar.value) return
  try {
    if (comissao.idPedidoVendaComissao && comissao.idPedidoVendaComissao > 0) {
      await api.delete(`/PedidoVendaComissoes/${comissao.idPedidoVendaComissao}`)
    }
    comissoes.value = comissoes.value.filter(c => c._key !== comissao._key)
    emit('show-notification', { message: 'Comissão removida.', type: 'success' })
  } catch (e) {
    const data = e.response?.data
    const msg = data?.message || 'Erro ao remover comissão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const onVendedorComissaoSelecionado = async (comissao, itemSelecionado) => {
  try {
    const res = await api.get(`/ParceiroVendedores/${itemSelecionado.idParceiro}`)
    const vendedor = res.data
    comissao._percentualBase = vendedor.percentualComissaoComoVendedor
    comissao.percentualAplicado = vendedor.percentualComissaoComoVendedor
    if (!comissao.lstPapelComercial) {
      comissao.lstPapelComercial = vendedor.lstPapelComercialPadrao || ''
    }

    // Salva o vendedor na comissão (cria ou atualiza)
    const payload = {
      idParceiroVendedor: itemSelecionado.idParceiro,
      lstPapelComercial: comissao.lstPapelComercial || '',
      percentualAplicado: comissao.percentualAplicado,
      percentualRateio: comissao.percentualRateio,
      valorProjetado: comissao.valorProjetado || 0
    }

    if (!comissao.idPedidoVendaComissao || comissao.idPedidoVendaComissao === 0) {
      const response = await api.post(`/PedidoVendaComissoes/${item.value.idPedidoVenda}`, payload)
      Object.assign(comissao, response.data, { _key: comissao._key })
    } else {
      payload.xmin = comissao.xmin
      const response = await api.put(`/PedidoVendaComissoes/${comissao.idPedidoVendaComissao}`, payload)
      Object.assign(comissao, response.data, { _key: comissao._key })
    }

    // Persiste o pedido e recalcula tudo (percentuais, gestores, valorProjetado)
    await salvarAlteracoes()
    await recalcularComissoes()
    emit('show-notification', { message: 'Vendedor salvo e comissões recalculadas!', type: 'success' })
  } catch (e) {
    const data = e.response?.data
    const msg = data?.message || 'Erro ao carregar dados do vendedor.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// Persiste a exclusão do vendedor (idParceiroVendedor = null) e regenera os gestores.
// Acionado quando o usuário limpa o CampoBusca do vendedor (não apenas pelo ícone de lixeira).
const onVendedorComissaoLimpo = async (comissao) => {
  if (!comissao.idPedidoVendaComissao || comissao.idPedidoVendaComissao === 0) return
  try {
    const payload = {
      idParceiroVendedor: null,
      lstPapelComercial: comissao.lstPapelComercial,
      percentualAplicado: 0,
      percentualRateio: comissao.percentualRateio,
      valorProjetado: 0,
      xmin: comissao.xmin
    }
    const response = await api.put(`/PedidoVendaComissoes/${comissao.idPedidoVendaComissao}`, payload)
    Object.assign(comissao, response.data, { _key: comissao._key })
    await recalcularComissoes()
    emit('show-notification', { message: 'Vendedor removido e gestores atualizados.', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao remover vendedor.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// Detecta alteração do vendedor da comissão. Quando o campo é limpo (valor nulo),
// persiste a exclusão e regenera os gestores automaticamente.
const onComissaoVendedorAlterado = (comissao, valor) => {
  const tinhaVendedor = !!comissao.idParceiroVendedor
  comissao.idParceiroVendedor = valor
  const ficouVazio = valor === null || valor === undefined || valor === ''
  if (tinhaVendedor && ficouVazio) {
    onVendedorComissaoLimpo(comissao)
  }
}

// --- Salva alterações de rateio (Split) e recalcula tudo em cadeia ---
const salvarComissao = async (comissao) => {
  if (!comissao.idPedidoVendaComissao || comissao.idPedidoVendaComissao === 0) return
  try {
    // Salva o rateio no backend
    const payload = {
      idParceiroVendedor: comissao.idParceiroVendedor,
      lstPapelComercial: comissao.lstPapelComercial,
      percentualAplicado: comissao.percentualAplicado,
      percentualRateio: comissao.percentualRateio,
      valorProjetado: comissao.valorProjetado,
      xmin: comissao.xmin
    }
    await api.put(`/PedidoVendaComissoes/${comissao.idPedidoVendaComissao}`, payload)

    // Recalcula tudo em cadeia: percentuais, gestores e valorProjetado
    await recalcularComissoes()
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao salvar comissão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Salva todos os rateios de uma vez (Split) e recalcula tudo ---
const salvandoRateios = ref(false)
const salvarTodosRateios = async () => {
  if (salvandoRateios.value) return
  salvandoRateios.value = true
  try {
    for (const comissao of comissoesSemGestor.value) {
      if (comissao.idPedidoVendaComissao && comissao.idPedidoVendaComissao > 0) {
        const payload = {
          idParceiroVendedor: comissao.idParceiroVendedor,
          lstPapelComercial: comissao.lstPapelComercial,
          percentualAplicado: comissao.percentualAplicado,
          percentualRateio: comissao.percentualRateio,
          valorProjetado: comissao.valorProjetado,
          xmin: comissao.xmin
        }
        await api.put(`/PedidoVendaComissoes/${comissao.idPedidoVendaComissao}`, payload)
      }
    }
    // Recalcula tudo em cadeia: percentuais, gestores e valorProjetado
    await recalcularComissoes()
    emit('show-notification', { message: 'Rateios e gestores atualizados!', type: 'success' })
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao salvar rateios.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    salvandoRateios.value = false
  }
}

// Gera config de busca por linha, passando o papel comercial como filtro default
const criarConfigBuscaPorComissao = (comissao) => {
  const papel = comissao?.lstPapelComercial || null
  return criarConfigBuscaParceiroVendedor(authStore.organizacaoAtiva, papel)
}

// Watch para recarregar comissões quando o pedido for salvo
watch(() => item.value.idPedidoVenda, (novoId) => {
  if (novoId && novoId > 0) {
    carregarComissoes()
  } else {
    comissoes.value = []
  }
}, { immediate: true })

// Watch para carregar comissões quando carregar um pedido existente
watch(() => props.itemParaEditar, async (newVal) => {
  if (newVal && newVal.idPedidoVenda) {
    await carregarComissoes()
  }
}, { deep: true })

// Watch: quando o valor total do pedido muda, recalcula valorProjetado das comissões.
// Silencioso: é um efeito colateral automático (ex: alteração de quantidade de um
// item) — a notificação de êxito exibida deve ser a do campo que o usuário editou.
watch(() => item.value.valorTotalNegociado, async (novoValor, valorAntigo) => {
  if (!item.value.idPedidoVenda || !novoValor || novoValor === valorAntigo) return
  if (comissoes.value.length > 0) {
    await recalcularComissoes(true)
  }
})
</script>

<template>
  <div class="pedido-venda-cadastro-wrapper" v-bind="$attrs">
    <SemAcessoModulo v-if="!temModuloPedidoVenda" titulo="Pedido de Venda" />
    <template v-if="temModuloPedidoVenda">
    <FormularioCadastro
      module-name="PedidoVenda"
      :is-loading="isLoading"
      :is-editing="isEditing"
      :entity-info="entityInfo"
      :status="getTextoLista(listas.LstStatusWorkflowPedido, item?.lstStatusWorkflowPedido)"
      :titulo-registro="item?.numeroPedidoVenda"
      :ocultar-salvar="!isPedidoSaved || !podeEditar"
      @submit="salvar"
      @submit-continuar="salvarAlteracoes"
      @cancelar="handleCancel"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <template #info-extra v-if="isEditing && item.idOperacao">
        <div class="info-item">
          <strong>Operação:</strong>
          <span>{{ descricaoOperacaoHeader }}</span>
        </div>
      </template>
      <template #acoes-header v-if="isEditing">
        <button
          type="button"
          class="btn-icon-minimalista me-3"
          @click="mostrarAuditoria = true"
          title="Ver Histórico de Alterações"
        >
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <!-- ============================================================ -->
      <!-- 🔝 FRAME FIXO SUPERIOR: Grid de Pedidos do Contrato + Status + Botões -->
      <!-- ============================================================ -->

      <!-- GRID DE PEDIDOS DO CONTRATO (colapsável, só se contrato) -->
      <div v-if="idContratoEfetivo" class="card mb-4 border-success-subtle shadow-sm">
        <div class="card-header bg-success-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleContratosVendaGrid">
          <h6 class="mb-0 text-success-emphasis fw-bold d-flex align-items-center">
            <i class="bi bi-file-earmark-text me-2"></i>Pedidos do Contrato
            <span v-if="pedidosDoContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
              {{ pedidosDoContrato.length }} {{ pedidosDoContrato.length === 1 ? 'pedido' : 'pedidos' }}
            </span>
          </h6>
          <div class="d-flex align-items-center gap-2">
            <button
              v-if="!isFormBloqueado"
              type="button"
              class="btn btn-sm btn-success text-white"
              @click.stop="iniciarNovoPedido"
            >
              <i class="bi bi-plus-lg"></i> Adicionar Pedido
            </button>
            <i class="bi fs-5" :class="contratosVendaGridAberto ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
        </div>
        <div v-show="contratosVendaGridAberto" class="card-body p-0">
          <div class="grid-scroll-container">
            <div v-if="isLoadingPedidosContrato" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-success me-2"></div>
              Carregando pedidos...
            </div>
            <table v-else class="table table-hover admin-table mb-0">
              <thead>
                <tr>
                  <th style="width: 120px;">Nº Pedido</th>
                  <th>Cliente</th>
                  <th style="width: 90px;">Emissão</th>
                  <th class="text-end" style="width: 130px;">Valor Negociado</th>
                  <th style="width: 110px;">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pd in pedidosDoContrato" :key="pd.idPedidoVenda" class="cursor-pointer" @click="selecionarPedido(pd)">
                  <td><code class="fw-medium text-body-emphasis">{{ pd.numeroPedidoVenda || '---' }}</code></td>
                  <td>{{ pd.nomeCliente || '---' }}</td>
                  <td>{{ formatarDataBR(pd.dataEmissao) }}</td>
                  <td class="text-end fw-bold">{{ formatarDecimal(pd.valorTotalNegociado) }}</td>
                  <td>
                    <span class="badge" :class="obterCorStatus(getTextoLista(listas.LstStatusWorkflowPedido, pd.lstStatusWorkflowPedido))">
                      {{ getTextoLista(listas.LstStatusWorkflowPedido, pd.lstStatusWorkflowPedido) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="pedidosDoContrato.length === 0">
                  <td colspan="5" class="text-center py-4 text-muted">Nenhum pedido de venda vinculado a este contrato.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- MODO ESPERA: Aviso -->
      <div v-if="modoEspera" class="alert alert-success border-0 rounded-3 mb-4 d-flex align-items-center">
        <i class="bi bi-info-circle-fill fs-4 me-3 text-success"></i>
        <div>
          <strong>Aguardando decisão:</strong>
          Clique em "Adicionar Pedido" no grid acima para decidir se deseja pré-preencher os dados com as informações do contrato.
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- 🔝 TOOLBAR DE AÇÕES DO PEDIDO -->
      <!-- ============================================================ -->
      <div v-if="isPedidoSaved" class="d-flex justify-content-between align-items-center gap-2 mb-3">
        <!-- LADO ESQUERDO: Cancelar (antes de FaturadoParcial) ou Concluir (a partir de FaturadoParcial) -->
        <div class="d-flex align-items-center gap-2">
          <button
            v-if="podeCancelar"
            type="button"
            class="btn btn-sm btn-outline-danger fw-bold"
            :disabled="isLoading"
            @click="confirmarTransicao(handleCancelarPedido, 'Deseja cancelar o pedido?')"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-x-lg me-1"></i> Cancelar
          </button>
          <button
            v-if="mostrarConcluir"
            type="button"
            class="btn btn-sm btn-secondary fw-bold"
            :disabled="isLoading"
            @click="handleEncerrarPedido"
          >
            <i class="bi bi-check2-square me-1"></i> Concluir
          </button>
        </div>

        <!-- LADO DIREITO: Botões de avanço de status -->
        <div class="d-flex gap-2">
          <!-- EmDigitação sem negociação → Gerar Orçamento -->
          <button
            v-if="mostrarGerarOrcamento"
            type="button"
            class="btn btn-sm btn-outline-success fw-bold"
            :disabled="isLoading"
            @click="confirmarTransicao(handleGerarOrcamento, 'Deseja gerar o orçamento?')"
          >
            <i class="bi bi-file-earmark-text me-1"></i> Gerar Orçamento
          </button>

          <!-- EmDigitação com negociação → Solicitar Aprovação Comercial -->
          <button
            v-if="mostrarSolicitarAprovComercial"
            type="button"
            class="btn btn-sm btn-outline-info fw-bold"
            :disabled="isLoading"
            @click="confirmarTransicao(handleDispararSolicitacaoComercial, 'Deseja solicitar aprovação comercial?')"
          >
            <i class="bi bi-check2-circle me-1"></i> Solicitar Aprovação Comercial
          </button>

          <!-- Aprovado Comercial → Fechar Orçamento -->
          <button
            v-if="mostrarFecharOrcamento"
            type="button"
            class="btn btn-sm btn-success fw-bold"
            :disabled="isLoading"
            @click="confirmarTransicao(handleFecharOrcamento, 'Deseja fechar o orçamento?')"
          >
            <i class="bi bi-send me-1"></i> Fechar Orçamento
          </button>

          <!-- Orçamento → Proforma Invoice (exportação) ou Orçamento de Venda (interno) -->
          <button
            v-if="mostrarBotaoDocumentoOrcamento && isExportacao"
            type="button"
            class="btn btn-sm btn-outline-primary fw-bold"
            :disabled="isLoading"
            @click="handleProformaInvoice"
          >
            <i class="bi bi-file-earmark-text me-1"></i> Proforma Invoice
          </button>
          <button
            v-if="mostrarBotaoDocumentoOrcamento && !isExportacao"
            type="button"
            class="btn btn-sm btn-outline-primary fw-bold"
            :disabled="isLoading"
            @click="handleOrcamentoVenda"
          >
            <i class="bi bi-file-earmark-text me-1"></i> Orçamento de Venda
          </button>

          <!-- Orçamento → Confirmar Pedido -->
          <button
            v-if="mostrarConfirmarPedido"
            type="button"
            class="btn btn-sm btn-success fw-bold"
            :disabled="isLoading"
            @click="confirmarTransicao(handleConfirmarPedido, 'Deseja confirmar o pedido?')"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check2-square me-1"></i> Confirmar Pedido
          </button>

          <!-- Faturado Parcial → Encerrar Pedido com Saldo -->
          <button
            v-if="mostrarEncerrarComSaldo"
            type="button"
            class="btn btn-sm btn-warning fw-bold"
            :disabled="isLoading"
            @click="handleEncerrarComSaldo"
          >
            <i class="bi bi-archive me-1"></i> Encerrar Pedido com Saldo
          </button>

          <!-- Reprovado Comercial → Encerrar Pedido -->
          <button
            v-if="mostrarEncerrarPedido"
            type="button"
            class="btn btn-sm btn-secondary fw-bold"
            :disabled="isLoading"
            @click="handleEncerrarPedido"
          >
            <i class="bi bi-check2-square me-1"></i> Encerrar Pedido
          </button>

          <!-- Reprovado Comercial / Bloqueado por Crédito / Cancelado → Reabrir Pedido -->
          <button
            v-if="mostrarReabrirPedido"
            type="button"
            class="btn btn-sm btn-outline-warning fw-bold"
            :disabled="isLoading"
            @click="handleReabrirPedido"
          >
            <i class="bi bi-arrow-counterclockwise me-1"></i> Reabrir Pedido
          </button>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- FRAME: DADOS BÁSICOS (campos obrigatórios do modelo)          -->
      <!-- ============================================================ -->
      <div class="card mb-4 border-success-subtle shadow-sm">
        <div class="card-header bg-success-subtle py-2 cursor-pointer d-flex justify-content-between align-items-center"
             @click="accordionState.dadosBasicos = !accordionState.dadosBasicos">
          <h6 class="mb-0 text-success-emphasis fw-bold"><i class="bi bi-file-earmark-text me-2"></i>Dados Básicos</h6>
          <i class="bi fs-5" :class="accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div class="card-body" v-show="accordionState.dadosBasicos">
          <div class="row g-3">
            <div class="col-md-2">
              <CampoBusca
                id="idFilial"
                label="Filial de Faturamento"
                v-model="item.idFilial"
                :configuracaoBusca="configBuscaFilial"
                required
                :disabled="campoDesabilitado('idFilial')"
                @selecionado="(val) => { item.nomeFilial = val?.identificacao || '' }"
              />
            </div>
            <div class="col-md-4">
              <CampoBusca
                id="idParceiroNegocioCliente"
                label="Cliente"
                v-model="item.idParceiroNegocioCliente"
                :configuracaoBusca="configBuscaCliente"
                required
                :disabled="campoDesabilitado('idParceiroNegocioCliente')"
              />
            </div>
            <div class="col-md-3">
              <CampoBusca
                id="idOperacao"
                label="Operação"
                v-model="item.idOperacao"
                :configuracaoBusca="configBuscaOperacao"
                required
                :disabled="campoDesabilitado('idOperacao')"
                @selecionado="onOperacaoSelecionada"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="numeroPedidoVenda"
                label="Nº Pedido"
                v-model="item.numeroPedidoVenda"
                disabled
              />
            </div>
            <div class="col-md-2">
              <CampoBuscaLista
                id="lstMoeda"
                label="Moeda"
                v-model="item.lstMoeda"
                tabela="PedidoVenda"
                required
                :disabled="campoDesabilitado('lstMoeda')"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="dataEmissao"
                label="Data Emissão"
                v-model="item.dataEmissao"
                type="date"
                disabled
              />
            </div>
            <div class="col-md-4">
              <CampoBuscaLista
                id="lstModalidadeVenda"
                label="Modalidade de Venda"
                v-model="item.lstModalidadeVenda"
                tabela="PedidoVenda"
                required
                :disabled="campoDesabilitado('lstModalidadeVenda')"
              />
            </div>
            <div class="col-md-2">
              <CampoSelect
                id="lstStatusWorkflowPedido"
                label="Workflow"
                v-model="item.lstStatusWorkflowPedido"
                :options="listas.LstStatusWorkflowPedido || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                disabled
              />
            </div>
            <div class="col-md-2">
              <CampoSelect
                id="lstStatusPedido"
                label="Status"
                v-model="item.lstStatusPedido"
                :options="listas.LstStatusPedido || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- BOTÃO SALVAR E CONTINUAR                                      -->
      <!-- ============================================================ -->
      <div class="mb-4 text-center" v-if="!isPedidoSaved && podeEditar && !modoEspera">
        <button
          type="button"
          @click.prevent="salvarEContinuar"
          class="btn btn-success text-white px-4 py-2 fw-bold"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i v-if="!isLoading" class="bi bi-chevron-double-down me-1"></i>
          Salvar e Continuar
          <i v-if="!isLoading" class="bi bi-chevron-double-down ms-1"></i>
        </button>
        <div class="form-text mt-2 text-success">
          <i class="bi bi-info-circle me-1"></i> Salve os dados básicos para habilitar o cadastro de Itens.
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- ABAS: Comercial, Dados da Operação, Pricing, Câmbio          -->
      <!-- ============================================================ -->
      <fieldset class="mb-3">
        <div class="card border-success-subtle shadow-sm">
          <div class="card-header bg-success-subtle py-0 px-0 border-bottom-0 cursor-pointer d-flex justify-content-between align-items-start"
               @click="toggleAbasHeader">
            <ul class="nav nav-tabs mb-0 px-3 pt-2 flex-grow-1" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'comercial' }"
                        @click="abaAtiva = 'comercial'" type="button" role="tab">
                  <i class="bi bi-briefcase me-1"></i> Comercial
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'dadosOperacao' }"
                        @click="abaAtiva = 'dadosOperacao'" type="button" role="tab">
                  <i class="bi bi-info-circle me-1"></i> Dados da Operação
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'pricing' }"
                        @click="abaAtiva = 'pricing'" type="button" role="tab">
                  <i class="bi bi-currency-dollar me-1"></i> Pricing & Valores
                </button>
              </li>
              <li class="nav-item" role="presentation" v-if="isExportacao">
                <button class="nav-link" :class="{ active: abaAtiva === 'cambio' }"
                        @click="abaAtiva = 'cambio'" type="button" role="tab">
                  <i class="bi bi-currency-exchange me-1"></i> Câmbio
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="{ active: abaAtiva === 'parceiros' }"
                        @click="abaAtiva = 'parceiros'" type="button" role="tab">
                  <i class="bi bi-people me-1"></i> Parceiros do Pedido
                </button>
              </li>
              <li class="nav-item" role="presentation" v-if="isPedidoSaved">
                <button class="nav-link" :class="{ active: abaAtiva === 'aprovacoes' }"
                        @click="abaAtiva = 'aprovacoes'" type="button" role="tab">
                  <i class="bi bi-check2-circle me-1"></i> Aprovações
                </button>
              </li>
            </ul>
            <i class="bi fs-5 pt-2 pe-3 flex-shrink-0"
               :class="accordionState.abas ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div class="card-body" v-show="accordionState.abas">
            <div class="tab-content">
              <!-- ============================================================ -->
              <!-- ABA: Comercial (primeira) -->
              <!-- ============================================================ -->
              <div v-show="abaAtiva === 'comercial'" role="tabpanel">
                <div class="row g-3">
                  <div class="col-md-4" v-if="!isExportacao">
                    <CampoBusca
                      id="idCondicaoPagamento"
                      label="Condição de Pagamento"
                      v-model="item.idCondicaoPagamento"
                      :configuracao-busca="configBuscaCondicaoPagamento"
                      :disabled="campoDesabilitado('idCondicaoPagamento')"
                    />
                  </div>
                  <!-- Forma de Pagamento: Nacional ou Internacional conforme regime -->
                  <div v-if="!isExportacao" class="col-md-4">
                    <CampoBuscaLista
                      key="formaPagamentoNacional"
                      id="lstFormaPagamento"
                      label="Forma de Pagamento (Nacional)"
                      v-model="item.lstFormaPagamento"
                      tabela="PedidoVenda"
                      :disabled="campoDesabilitado('lstFormaPagamento')"
                    />
                  </div>
                  <div v-if="isExportacao" class="col-md-4">
                    <CampoBuscaLista
                      key="formaPagamentoInternacional"
                      id="lstFormaPagamentoInternacional"
                      label="Forma de Pagamento (Internacional)"
                      v-model="item.lstFormaPagamentoInternacional"
                      tabela="PedidoVenda"
                      :disabled="campoDesabilitado('lstFormaPagamentoInternacional')"
                    />
                  </div>
                  <div class="col-md-3">
                    <CampoInput
                      id="percentualTolerancia"
                      label="Tolerância Quantitativa (%)"
                      v-model="item.percentualTolerancia"
                      type="percentual"
                      :disabled="campoDesabilitado('percentualTolerancia')"
                    />
                  </div>
                </div>

                <div class="row g-3 mt-2">
                  <div class="col-md-4">
                    <CampoBuscaLista
                      id="lstModoPrecificacao"
                      label="Modo de Precificação"
                      v-model="item.lstModoPrecificacao"
                      tabela="PedidoVenda"
                      :disabled="modoPrecificacaoTravado || campoDesabilitado('lstModoPrecificacao')"
                    />
                  </div>
                  <div class="col-md-4">
                    <CampoBusca
                      id="idPrecoComercial"
                      label="Tabela de Preços"
                      v-model="item.idPrecoComercial"
                      :configuracao-busca="configBuscaPrecoComercialCliente"
                      :disabled="!isModoPorTabela || modoPrecificacaoTravado || campoDesabilitado('idPrecoComercial') || !item.idParceiroNegocioCliente"
                    />
                  </div>
                </div>

                <div class="row g-3 mt-2">
                  <div class="col-md-12">
                    <CampoInput
                      id="observacaoInterna"
                      label="Observação Interna"
                      v-model="item.observacaoInterna"
                      type="textarea"
                      :max-length="500"
                      :disabled="false"
                    />
                  </div>
                </div>

                <!-- CONDIÇÕES DE PAGAMENTO DETALHADAS (Exportação) — único frame mantido -->
                <div v-if="isExportacao" class="card mt-4" :class="errors.condicoesPagamento ? 'border-danger' : 'border-success-subtle'">
                  <div class="card-header d-flex justify-content-between align-items-center" :class="errors.condicoesPagamento ? 'bg-danger-subtle' : 'bg-success-subtle'">
                    <h6 class="mb-0 fw-bold" :class="errors.condicoesPagamento ? 'text-danger-emphasis' : 'text-success-emphasis'">
                      <i class="bi bi-credit-card me-2"></i>Condições de Pagamento Detalhadas
                      <span v-if="errors.condicoesPagamento" class="ms-2 badge bg-danger text-white border border-danger">
                        <i class="bi bi-exclamation-triangle me-1"></i>Pendente
                      </span>
                      <span v-else-if="condicoesPagamento.length > 0" class="ms-3 fw-normal fs-6 badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
                        {{ condicoesPagamento.length }} {{ condicoesPagamento.length === 1 ? 'condição cadastrada' : 'condições cadastradas' }}
                      </span>
                    </h6>
                    <button
                      v-if="isPedidoSaved && !isFormBloqueado"
                      type="button"
                      class="btn btn-sm btn-success"
                      @click="iniciarNovaCondicao"
                    >
                      <i class="bi bi-plus-lg"></i> Adicionar Condição
                    </button>
                  </div>
                  <div class="card-body p-0 border-top border-success-subtle">
                    <div v-if="isLoadingCondicoes" class="text-center py-4">
                      <div class="spinner-border spinner-border-sm text-success me-2"></div>
                      Carregando condições...
                    </div>
                    <div v-else class="admin-table-container border-0 shadow-none mb-0">
                      <table class="table admin-table m-0">
                        <thead>
                          <tr>
                            <th>Descrição explicativa</th>
                            <th class="text-end" style="width: 100px;">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="cond in condicoesPagamento" :key="cond.idPedidoVendaCondicaoPg">
                            <td>{{ gerarDescricaoCondicao(cond) }}</td>
                            <td class="text-end pe-3">
                              <button v-if="!isFormBloqueado" type="button" class="btn-icon" @click="editarCondicao(cond)" title="Editar">
                                <i class="bi bi-pencil-fill"></i>
                              </button>
                              <button v-if="!isFormBloqueado" type="button" class="btn-icon text-danger" @click="solicitarExclusaoCondicao(cond)" title="Excluir">
                                <i class="bi bi-trash-fill"></i>
                              </button>
                            </td>
                          </tr>
                          <tr v-if="condicoesPagamento.length === 0">
                            <td colspan="2" class="text-center py-4 text-muted">
                              <i class="bi bi-info-circle me-2"></i> Nenhuma condição de pagamento cadastrada.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ============================================================ -->
              <!-- ABA: Dados da Operação -->
              <!-- ============================================================ -->
              <div v-show="abaAtiva === 'dadosOperacao'" role="tabpanel">

                <!-- Seção Endereços -->
                <h6 class="text-success-emphasis fw-bold mb-3 mt-1">
                  <i class="bi bi-geo-alt me-2"></i>Endereços
                </h6>

                <!-- Linha 1: Parceiro de Faturamento + Endereço de Faturamento -->
                <div class="row g-3 mb-3">
                  <div class="col-md-6">
                    <CampoInput
                      id="nomeClienteFaturamento"
                      label="Parceiro de Faturamento"
                      :model-value="item.nomeCliente || '---'"
                      disabled
                    />
                  </div>
                  <div class="col-md-6">
                    <CampoBusca
                      id="idParceiroEnderecoFaturamento"
                      label="Endereço de Faturamento"
                      v-model="item.idParceiroEnderecoFaturamento"
                      :configuracao-busca="configBuscaEnderecoFaturamento"
                      :disabled="campoDesabilitado('idParceiroEnderecoFaturamento')"
                    />
                  </div>
                </div>

                <!-- Linha 2: Parceiro de Remessa + Endereço de Remessa -->
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <label
                        class="form-label mb-0"
                        :for="operacaoTriangular ? 'idParceiroRemessa' : 'nomeClienteRemessa'"
                      >
                        Parceiro de Remessa
                      </label>
                      <div class="form-check form-switch mb-0 d-flex align-items-center">
                        <input
                          class="form-check-input cursor-pointer"
                          type="checkbox"
                          id="operacaoTriangular"
                          v-model="operacaoTriangular"
                          :disabled="campoDesabilitado('operacaoTriangular')"
                        />
                        <label class="form-check-label label-operacao-triangular ms-1 cursor-pointer mb-0" for="operacaoTriangular">
                          Operação Triangular
                        </label>
                      </div>
                    </div>
                    <template v-if="operacaoTriangular">
                      <CampoBusca
                        id="idParceiroRemessa"
                        hide-label
                        v-model="item.idParceiroRemessa"
                        :configuracao-busca="{
                          ...parceiroSearch,
                          titulo: 'Selecionar Parceiro de Remessa',
                          filtrosFixos: { isCliente: true }
                        }"
                        :disabled="campoDesabilitado('idParceiroRemessa')"
                      />
                    </template>
                    <template v-else>
                      <CampoInput
                        id="nomeClienteRemessa"
                        hide-label
                        :model-value="item.nomeCliente || '---'"
                        disabled
                      />
                    </template>
                  </div>
                  <div class="col-md-6">
                    <CampoBusca
                      id="idParceiroEnderecoEntrega"
                      label="Endereço de Remessa"
                      v-model="item.idParceiroEnderecoEntrega"
                      :configuracao-busca="configBuscaEnderecoEntrega"
                      :disabled="campoDesabilitado('idParceiroEnderecoEntrega')"
                    />
                  </div>
                </div>

                <!-- Incoterm (Exportação) -->
                <template v-if="isExportacao">
                  <hr class="my-4">
                  <h6 class="text-success-emphasis fw-bold mb-3">
                    <i class="bi bi-briefcase me-2"></i>Incoterm
                  </h6>
                  <div class="row g-3">
                    <div class="col-md-6">
                      <CampoBuscaLista
                        id="lstIncoterm"
                        label="Incoterm"
                        v-model="item.lstIncoterm"
                        tabela="PedidoVenda"
                        :disabled="campoDesabilitado('lstIncoterm')"
                      />
                    </div>
                    <div class="col-md-6">
                      <CampoBuscaLista
                        id="lstLocalIncoterm"
                        label="Local Incoterm"
                        v-model="item.lstLocalIncoterm"
                        tabela="PedidoVenda"
                        :disabled="campoDesabilitado('lstLocalIncoterm')"
                      />
                    </div>
                  </div>
                </template>
              </div>

            <!-- ============================================================ -->
            <!-- ABA: Pricing & Valores -->
            <!-- ============================================================ -->
            <div v-show="abaAtiva === 'pricing'" role="tabpanel">

              <!-- Precificação -->
              <div class="mb-3 mt-1">
                <h6 class="text-success-emphasis fw-bold mb-0 d-flex align-items-center gap-2 flex-wrap">
                  <i class="bi bi-currency-dollar me-2"></i>Precificação
                  <button
                    v-if="!isFormBloqueado"
                    ref="btnTemplate"
                    type="button"
                    class="btn btn-sm btn-success"
                    @click="abrirBuscaTemplate"
                  >
                    <i class="bi" :class="pricingFields.length > 0 ? 'bi-arrow-repeat' : 'bi-plus-lg'"></i>
                    {{ pricingFields.length > 0 ? 'Trocar Template' : 'Adicionar Template' }}
                  </button>
                </h6>
              </div>
              <div v-if="pricingFields.length === 0" class="text-muted small mb-4">
                <i class="bi bi-info-circle me-1"></i>Selecione um template de precificação para definir os atributos comerciais do pedido.
              </div>
              <div v-else class="row g-3 mb-4">
                <div v-for="campo in pricingFields" :key="campo.chave" class="col-md-4">
                  <CampoInput
                    :id="'pricing_' + campo.chave"
                    :label="campo.label || campo.chave"
                    v-model="valoresPricing[campo.chave]"
                    :disabled="campoDesabilitado('pricing_' + campo.chave)"
                  />
                </div>
              </div>

              <hr class="my-4">

              <!-- Breakdown de Valores -->
              <h6 class="text-success-emphasis fw-bold mb-3">
                <i class="bi bi-calculator me-2"></i>Breakdown de Valores
              </h6>
              <div class="row g-3">
                <div class="col-md-3">
                  <CampoInput
                    id="valorTotalOriginal"
                    label="Valor Produtos"
                    v-model="item.valorTotalOriginal"
                    type="moeda"
                    disabled
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="valorTotalFrete"
                    label="Valor Frete"
                    v-model="item.valorTotalFrete"
                    type="moeda"
                    :disabled="campoDesabilitado('valorTotalFrete')"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="valorTotalDesconto"
                    label="Valor Desconto"
                    v-model="item.valorTotalDesconto"
                    type="moeda"
                    :disabled="campoDesabilitado('valorTotalDesconto')"
                  />
                </div>
                <div class="col-md-3">
                  <CampoInput
                    id="valorTotalNegociado"
                    label="Valor Total"
                    v-model="item.valorTotalNegociado"
                    type="moeda"
                    disabled
                  />
                </div>
              </div>

              <hr class="my-4">

              <!-- Operação Financiada (título de divisor + switch colado) -->
              <h6 class="text-success-emphasis fw-bold mb-3 d-flex align-items-center gap-0">
                <i class="bi bi-currency-exchange me-2"></i>Operação Financiada
                <div class="form-check form-switch mb-0 d-flex align-items-center" style="margin-left: 0.25rem;">
                  <input
                    class="form-check-input switch-lg cursor-pointer"
                    type="checkbox"
                    role="switch"
                    id="operacaoFinanciada"
                    v-model="item.operacaoFinanciada"
                    :disabled="campoDesabilitado('operacaoFinanciada')"
                  />
                </div>
              </h6>

            </div>

            <!-- ============================================================ -->
            <!-- ABA: Câmbio -->
            <!-- ============================================================ -->
            <div v-show="abaAtiva === 'cambio'" role="tabpanel">
              <div class="row g-3">
                <!-- Linha de metadados (gerados automaticamente) -->
                <div class="col-12">
                  <div class="row g-3 mb-4 pb-3 border-bottom">
                    <div class="col-md-3">
                      <CampoBuscaLista
                        id="lstTipoTaxaConversao"
                        label="Tipo Taxa Conversão"
                        v-model="item.lstTipoTaxaConversao"
                        tabela="PedidoVenda"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente"
                      />
                    </div>
                    <div class="col-md-2">
                      <CampoInput
                        id="dataAlvoBuscaCambio"
                        label="Data Alvo Câmbio"
                        v-model="item.dataAlvoBuscaCambio"
                        type="date"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente"
                      />
                    </div>
                    <div class="col-md-2">
                      <CampoInput
                        id="dataReferenciaCambio"
                        label="Data Ref. Câmbio"
                        v-model="item.dataReferenciaCambio"
                        type="date"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente"
                      />
                    </div>
                    <div class="col-md-2">
                      <CampoBuscaLista
                        id="lstFonteCambio"
                        label="Fonte da Cotação"
                        v-model="item.lstFonteCambio"
                        tabela="PedidoVenda"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente"
                      />
                    </div>
                    <div class="col-md-3">
                      <CampoBuscaLista
                        id="lstTaxaCambioStatus"
                        label="Status Taxa"
                        v-model="item.lstTaxaCambioStatus"
                        tabela="PedidoVenda"
                        :disabled="true"
                        descricao-informativa="Campos de conversão são gerados automaticamente"
                      />
                    </div>
                  </div>
                </div>

                <!-- Mini-cards: Moeda Padrão vs Moeda Gerencial -->
                <div class="col-md-6">
                  <div class="card border-primary h-100">
                    <div class="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                      <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Padrão</h6>
                      <div class="d-flex gap-1">
                        <button v-if="isPedidoSaved && item.lstFonteCambio === MOE_FONTE_COTACAO.Manual"
                          type="button" class="btn btn-sm btn-outline-light py-0"
                          :disabled="isResolvendoTaxas" @click.stop="resolverTaxasAutomaticamente"
                          title="Buscar cotações automaticamente">
                          <i class="bi bi-robot me-1"></i> Definir Automaticamente
                        </button>
                        <button v-if="isPedidoSaved"
                          type="button" class="btn btn-sm btn-light py-0"
                          @click.stop="abrirModalCotacaoManual"
                          title="Definir manualmente as taxas de câmbio">
                          <i class="bi bi-pencil-square me-1"></i> Definir Manualmente
                        </button>
                      </div>
                    </div>
                    <div class="card-body py-2">
                      <div class="row g-2">
                        <div class="col-4">
                          <CampoBuscaLista
                            id="lstMoedaPadrao"
                            label="Moeda"
                            v-model="item.lstMoedaPadrao"
                            tabela="PedidoVenda"
                            :disabled="true"
                            descricao-informativa="Campos de conversão são gerados automaticamente"
                          />
                        </div>
                        <div class="col-4">
                          <CampoInput
                            id="taxaCambioPadrao"
                            :label="labelTaxaPadrao"
                            type="moeda"
                            :casas-decimais="6"
                            v-model="item.taxaCambioPadrao"
                            :disabled="true"
                          />
                        </div>
                        <div class="col-4">
                          <CampoInput
                            id="valorTotalNegociadoPadrao"
                            label="Valor Negociado"
                            type="moeda"
                            :model-value="item.valorTotalNegociadoPadrao"
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card border-primary h-100">
                    <div class="card-header bg-primary text-white py-2">
                      <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Gerencial</h6>
                    </div>
                    <div class="card-body py-2">
                      <div class="row g-2">
                        <div class="col-3">
                          <CampoBuscaLista
                            id="lstMoedaGerencial"
                            label="Moeda"
                            v-model="item.lstMoedaGerencial"
                            tabela="PedidoVenda"
                            :disabled="true"
                            descricao-informativa="Campos de conversão são gerados automaticamente"
                          />
                        </div>
                        <div class="col-3">
                          <CampoInput
                            id="taxaCambioGerencialPadrao"
                            :label="labelTaxaGerencialPadrao"
                            type="moeda"
                            :casas-decimais="6"
                            v-model="item.taxaCambioGerencialPadrao"
                            :disabled="true"
                          />
                        </div>
                        <div class="col-3">
                          <CampoInput
                            id="taxaCambioGerencial"
                            :label="labelTaxaCambioGerencial"
                            type="moeda"
                            :casas-decimais="6"
                            v-model="item.taxaCambioGerencial"
                            :disabled="true"
                          />
                        </div>
                        <div class="col-3">
                          <CampoInput
                            id="valorTotalNegociadoGerencial"
                            label="Valor Negociado"
                            type="moeda"
                            :model-value="item.valorTotalNegociadoGerencial"
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tabela de Breakdown (4 valores em 3 moedas) -->
                <div class="col-12">
                  <div class="card border-secondary mt-3">
                    <div class="card-header bg-body-tertiary py-2">
                      <h6 class="mb-0"><i class="bi bi-table me-1 text-primary"></i> Valores Convertidos (Breakdown)</h6>
                    </div>
                    <div class="card-body p-0">
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered admin-table mb-0">
                          <thead>
                            <tr>
                              <th style="width: 120px;">Breakdown</th>
                              <th class="text-end">Moeda Original ({{ obterSiglaMoeda(item.lstMoeda) || '-' }})</th>
                              <th class="text-end">Moeda Padrão ({{ obterSiglaMoeda(item.lstMoedaPadrao) || '-' }})</th>
                              <th class="text-end">Moeda Gerencial ({{ obterSiglaMoeda(item.lstMoedaGerencial) || '-' }})</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Valor Original</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalOriginal) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalOriginalPadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalOriginalGerencial) }}</td>
                            </tr>
                            <tr>
                              <td><strong>Frete</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalFrete) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalFretePadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalFreteGerencial) }}</td>
                            </tr>
                            <tr>
                              <td><strong>Desconto</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalDesconto) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalDescontoPadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalDescontoGerencial) }}</td>
                            </tr>
                            <tr class="table-active fw-bold">
                              <td><strong>Negociado</strong></td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalNegociado) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalNegociadoPadrao) }}</td>
                              <td class="text-end">{{ formatarMoeda(item.valorTotalNegociadoGerencial) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          <!-- ============================================================ -->
          <!-- ABA: Parceiros do Pedido -->
          <!-- ============================================================ -->
          <div v-show="abaAtiva === 'parceiros'" role="tabpanel">
            <!-- Parceiro Indicador (existente) -->
            <div class="row g-3">
              <div class="col-md-4">
                <CampoBusca
                  id="idParceiroIndicador"
                  label="Parceiro Indicador"
                  v-model="item.idParceiroIndicador"
                  :configuracao-busca="configBuscaIndicador"
                  :disabled="campoDesabilitado('idParceiroIndicador') || item.lstStatusWorkflowPedido !== PED_VEN_WORKFLOW_ST.EmDigitacao"
                />
              </div>
            </div>

            <!-- GESTORES — Apenas nomes (percentuais são confidenciais) -->
            <div v-if="gestores.length > 0" class="mb-3">
              <div v-for="(gestor, idx) in gestores" :key="'gestor-' + idx" class="row g-2 mb-1 align-items-center">
                <div class="col-md-4">
                  <CampoInput
                    :id="'nomeGestor_' + idx"
                    label="Gestor Comercial"
                    :model-value="gestor.nomeVendedor || '---'"
                    disabled
                  />
                </div>
              </div>

              <!-- Botão para regenerar gestores -->
              <div class="d-flex justify-content-end mt-2">
                <button
                  v-if="!isFormBloqueado && isPedidoSaved"
                  type="button"
                  class="btn btn-sm btn-outline-success"
                  :disabled="!temVendedoresDefinidos || isLoadingGestores"
                  @click="gerarGestores"
                >
                  <i class="bi bi-arrow-repeat me-1"></i>
                  <template v-if="isLoadingGestores">
                    <span class="spinner-border spinner-border-sm me-1"></span> Gerando...
                  </template>
                  <template v-else>
                    Recalcular Gestores
                  </template>
                </button>
              </div>
            </div>

            <!-- Botão para gerar gestores quando ainda não há nenhum -->
            <div v-else-if="temVendedoresDefinidos && isPedidoSaved && !isLoadingGestores" class="mb-3">
              <div class="d-flex justify-content-start">
                <button
                  v-if="!isFormBloqueado"
                  type="button"
                  class="btn btn-sm btn-success"
                  :disabled="isLoadingGestores"
                  @click="gerarGestores"
                >
                  <i class="bi bi-people me-1"></i>
                  <template v-if="isLoadingGestores">
                    <span class="spinner-border spinner-border-sm me-1"></span> Gerando...
                  </template>
                  <template v-else>
                    Gerar Gestores
                  </template>
                </button>
              </div>
            </div>

            <hr class="my-4">

            <!-- COMISSÕES — Definidor de Parceiros Recebedores de Comissão -->
            <div class="mb-3">
              <h6 class="text-success-emphasis fw-bold mb-0 d-flex align-items-center gap-2 flex-wrap">
                <i class="bi bi-cash-coin me-2"></i>Comissões
                <span v-if="comissoesSemGestor.length > 0" class="badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
                  {{ comissoesSemGestor.length }} {{ comissoesSemGestor.length === 1 ? 'comissão' : 'comissões' }}
                </span>
                <span v-if="item.lstModalidadeVenda === PED_VEN_MODALIDADE_VND.Autoatendimento" class="small text-muted d-flex align-items-center">
                  <i class="bi bi-info-circle me-1"></i>Autoatendimento não permite comissões
                </span>
                <span v-else-if="modoComissao === 'none'" class="small text-muted d-flex align-items-center">
                  <i class="bi bi-info-circle me-1"></i>Selecione a modalidade de venda para gerar comissões
                </span>

                <!-- Totalizador de rateio (Split) -->
                <div v-if="mostrarRateio && comissoesSemGestor.length > 0" class="mt-2 mb-2 d-flex align-items-center gap-2">
                  <span class="badge" :class="classeTotalizador">
                    Total rateio: {{ totalRateio.toFixed(2) }}% — deve ser 100%
                  </span>
                  <button
                    v-if="!isFormBloqueado"
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="salvandoRateios"
                    @click="salvarTodosRateios"
                  >
                    <template v-if="salvandoRateios">
                      <span class="spinner-border spinner-border-sm me-1"></span> Salvando...
                    </template>
                    <template v-else>
                      <i class="bi bi-check-lg me-1"></i> Salvar Rateios
                    </template>
                  </button>
                </div>

                <button
                  v-if="!isFormBloqueado && isPedidoSaved && podeAdicionarComissao"
                  type="button"
                  class="btn btn-sm btn-success"
                  @click="adicionarComissao"
                >
                  <i class="bi bi-plus-lg"></i> Comissão
                </button>
              </h6>
            </div>

            <div v-if="!isPedidoSaved" class="text-center py-3 text-muted">
              <i class="bi bi-info-circle me-1"></i> Salve o pedido primeiro para gerenciar comissões.
            </div>

            <div v-if="isLoadingComissao" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-success me-2"></div>
              Gerando comissões...
            </div>

            <template v-if="isPedidoSaved && !isLoadingComissao">
              <div v-for="(comissao, index) in comissoesSemGestor" :key="comissao._key" class="row g-3 mb-2 align-items-start">
                <div class="col-md-4">
                  <CampoBusca
                    :id="'idParceiroVendedorComissao_' + index"
                    label="Parceiro Vendedor"
                    :model-value="comissao.idParceiroVendedor"
                    :configuracao-busca="criarConfigBuscaPorComissao(comissao)"
                    :disabled="campoDesabilitado('comissaoVendedor')"
                    @update:model-value="(valor) => onComissaoVendedorAlterado(comissao, valor)"
                    @selecionado="(item) => onVendedorComissaoSelecionado(comissao, item)"
                  />
                </div>
                <div class="col-md-5 position-relative">
                  <CampoBuscaLista
                    id="lstPapelComercial"
                    label="Papel Comercial"
                    v-model="comissao.lstPapelComercial"
                    tabela="PedidoVendaComissao"
                    disabled
                  />
                </div>
                <!-- % Rateio: visível apenas na modalidade Split (definição do rateio) -->
                <div v-if="mostrarRateio" class="col-md-1">
                  <CampoInput
                    :id="'percentualRateioComissao_' + index"
                    label="% Rateio"
                    v-model="comissao.percentualRateio"
                    type="percentual"
                    :disabled="campoDesabilitado('comissaoPercentualRateio')"
                    @blur="salvarComissao(comissao)"
                  />
                </div>
                <div class="col-md-1 d-flex align-items-center justify-content-left" style="min-height: 75px;">
                  <button
                    v-if="!isFormBloqueado && podeAdicionarComissao"
                    type="button"
                    class="btn-icon text-danger"
                    @click="removerComissao(comissao)"
                    title="Excluir"
                  >
                    <i class="bi bi-trash-fill"></i>
                  </button>
                </div>
              </div>

              <div v-if="comissoesSemGestor.length === 0" class="text-center py-3 text-muted">
                <i class="bi bi-inbox me-1"></i> Nenhuma comissão cadastrada.
              </div>
            </template>
          </div>

          <!-- ABA: Aprovações -->
          <div v-show="abaAtiva === 'aprovacoes'" role="tabpanel"
               :class="errors.resumoAprovacao || errors.idGrupoAcessoAprovador ? 'border border-danger p-2 rounded-2' : ''">
            <WorkflowAprovacaoGrid
              ref="workflowGridRef"
              sem-frame
              entidade="PedidoVenda"
              :id-entidade="item.idPedidoVenda"
              :processos="processosAprovacao"
              :pode-solicitar="podeSolicitarAprovacao"
              :pode-analisar="authStore.pode('APROVACAO_VENDAS')"
              modo="parent-driven"
              modulo-regra-acesso="APROVACAO_VENDAS"
              @solicitacao-preenchida="handleSolicitacaoPreenchida"
              @show-notification="(p) => emit('show-notification', p)"
            />
          </div>

          </div><!-- /tab-content -->
        </div><!-- /card-body -->
      </div><!-- /card -->
    </fieldset>

      <!-- ============================================================ -->
      <!-- FRAME: Itens do Pedido -->
      <!-- ============================================================ -->
      <fieldset class="mb-3">
        <div class="card mb-3 border-success-subtle">
          <div class="card-header bg-success-subtle py-2 cursor-pointer d-flex justify-content-between align-items-center"
               @click="accordionState.itens = !accordionState.itens">
            <h6 class="mb-0 text-success-emphasis fw-bold">
              <i class="bi bi-cart me-2"></i>Itens do Pedido
              <span v-if="itensPedido.length > 0" class="ms-2 badge bg-success bg-opacity-10 text-success-emphasis border border-success-subtle">
                {{ itensPedido.length }} {{ itensPedido.length === 1 ? 'item' : 'itens' }}
              </span>
            </h6>
            <i class="bi fs-5" :class="accordionState.itens ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
          <div class="card-body p-0 border-top border-success-subtle" v-show="accordionState.itens">
            <div v-if="!isPedidoSaved" class="text-center py-5">
              <p class="text-muted mb-0">Salve o pedido primeiro para acessar os Itens.</p>
            </div>
            <div v-else>
              <!-- Filtros de Local de Estoque Padrão -->
              <div class="px-3 py-2 bg-body-tertiary border-bottom">
                <div class="row g-2 align-items-start">
                  <div class="col-md-3">
                    <CampoBusca
                      id="idFilialPadrao"
                      label="Filial Padrão"
                      v-model="idFilialPadrao"
                      :configuracao-busca="configBuscaFilial"
                      :disabled="campoDesabilitado('idFilialPadrao')"
                    />
                  </div>
                  <div class="col-md-4">
                    <CampoBusca
                      id="idLocalEstoquePadrao"
                      label="Local Estoque Padrão"
                      v-model="idLocalEstoquePadrao"
                      :configuracao-busca="configBuscaLocalEstoquePadrao"
                      :disabled="campoDesabilitado('idLocalEstoquePadrao')"
                      @selecionado="onLocalEstoquePadraoSelecionado"
                    />
                  </div>
                  <div class="col-md-3">
                    <CampoInput
                      id="dataPrevisaoEntregaPadrao"
                      label="Previsão Entrega Padrão"
                      v-model="dataPrevisaoEntregaPadrao"
                      type="date"
                      :disabled="campoDesabilitado('dataPrevisaoEntregaPadrao')"
                    />
                  </div>
                </div>
              </div>
              <PedidoVendaItensExcel
                v-if="isPedidoSaved"
                ref="itensExcelRef"
                :key="item.idPedidoVenda"
                :id-pedido-venda="item.idPedidoVenda"
                :itens="itensPedido"
                :listas="listas"
                :pode-editar="podeEditar"
                :is-pos-aprovacao="sequenciaAtual >= SEQUENCIA_FATURADO_PARCIAL"
                :read-only-workflow="isReadOnly"
                :lst-moeda="item.lstMoeda"
                :lst-moeda-padrao="item.lstMoedaPadrao"
                :lst-moeda-gerencial="item.lstMoedaGerencial"
                :id-filial="item.idFilial"
                :descricao-filial="item.nomeFilial || ''"
                :id-organizacao="authStore.organizacaoAtiva ? Number(authStore.organizacaoAtiva) : null"
                :id-local-estoque-padrao="idLocalEstoquePadrao"
                :descricao-local-estoque-padrao="descricaoLocalEstoquePadrao"
                :data-previsao-entrega-padrao="dataPrevisaoEntregaPadrao"
                :lst-modo-precificacao="item.lstModoPrecificacao"
                :id-preco-comercial="item.idPrecoComercial"
                :lst-status-workflow-pedido="item.lstStatusWorkflowPedido"
                :status-reserva-documento="statusReservaDocumento"
                :bloqueado-aprovacao="item.lstStatusAprovacaoComercial === WORK_APR_ST.Pendente"
                @save-item="handleSalvarItem"
                @save-all-items="handleSalvarTodosItens"
                @delete-item="handleRemoverItem"
                @cancel-item="handleCancelarItem"
                @show-notification="(p) => emit('show-notification', p)"
              />
            </div>
          </div>
        </div>
      </fieldset>

      <!-- ============================================================ -->
      <!-- Cláusulas e Termos -->
      <!-- ============================================================ -->
      <fieldset class="mb-3">
        <div v-if="isPedidoSaved" class="card border-success-subtle shadow-sm" :class="{'border border-danger p-1 rounded-2': errors.clausulas}">
          <ClausulasGrid
            ref="clausulasGridRef"
            :id-documento="item.idPedidoVenda"
            tipo-documento="PV"
            :lst-aplicacao-clausula="CLAUSULAS_COM_APLIC.PV"
            :pode-editar="podeEditar"
            :ids-produtos="itensPedido.map(i => ({ idProduto: i.idProduto, sku: i.sku, nomeProduto: i.nomeProduto }))"
            :ids-parceiros="item.idParceiroNegocioCliente ? [{ idParceiro: item.idParceiroNegocioCliente, nomeParceiro: item.nomeCliente }] : []"
            :clausulas-inicializadas="item.clausulasInicializadas"
            :com-accordion="true"
            :accordion-aberto="accordionState.clausulas"
            cor-header-class="bg-success-subtle"
            @update:accordion-aberto="accordionState.clausulas = $event"
            @show-notification="(p) => emit('show-notification', p)"
          />
        </div>
        <div v-else class="text-center py-4 text-muted mb-3">
          <i class="bi bi-info-circle me-2"></i> Salve o pedido primeiro para gerenciar cláusulas.
        </div>
      </fieldset>

      <!-- AUDITORIA -->
      <AuditLogViewer
        v-if="mostrarAuditoria"
        tabela="PedidoVenda"
        :chave-primaria="item.idPedidoVenda"
        :titulo-registro="item.numeroPedidoVenda"
        :dicionarios="{ LstStatusPedido: listas.LstStatusPedido || [] }"
        :tabelas-filhas="['PedidoVendaProduto', 'PedidoVendaCondicaoPg', 'PedidoVendaClausula']"
        chave-estrangeira="IdPedidoVenda"
        contexto="tenant"
        @fechar="mostrarAuditoria = false"
      />
    </FormularioCadastro>
    </template>

    <!-- MODAIS DE PRICING (TEMPLATE DE PRECIFICAÇÃO) -->
    <ModalDeBusca
      v-if="modalBuscaTemplate"
      :configuracao="configBuscaTemplate"
      @item-selecionado="onTemplateSelecionado"
      @fechar="modalBuscaTemplate = false"
    />

    <ConfirmationModal
      ref="modalConfirmacaoTrocaTemplateRef"
      title="Trocar Template"
      message="Ao trocar o template, os valores preenchidos no pricing atual serão perdidos. Deseja continuar?"
      @confirmed="confirmarTrocaTemplate"
    />

    <ConfirmationModal
      ref="confirmationModalRef"
      title="Alterações não salvas"
      message="Existem alterações no formulário que serão perdidas se você continuar sem salvar. Deseja realmente continuar?"
      confirm-button-text="Sim, descartar alterações"
      cancel-button-text="Não, voltar e salvar"
      @confirmed="onConfirmTransition"
    />

    <!-- MODAL DE CONFIRMAÇÃO PARA PRÉ-PREENCHIMENTO -->
    <ConfirmationModal
      ref="prePreencherModalRef"
      title="Pré-preencher Pedido de Venda?"
      message="Deseja pré-preencher o Pedido de Venda com os dados do Contrato?<br><br>Se sim, os dados básicos (Filial, Cliente, Moeda) serão herdados do contrato e, ao salvar, os produtos e condições de pagamento do contrato serão automaticamente copiados para este Pedido."
      confirm-button-text="Sim, pré-preencher"
      cancel-button-text="Não, começar vazio"
      @confirmed="(confirmed) => confirmed ? iniciarComPrePreencher() : iniciarSemPrePreencher()"
    />

    <!-- MODAIS DE CONFIRMAÇÃO PARA AÇÕES DO REPROVADO COMERCIAL -->
    <ConfirmationModal
      ref="confirmacaoEncerrarRef"
      title="Encerrar Pedido"
      message="Tem certeza que deseja encerrar este pedido?<br><br>O status do pedido será alterado para <strong>Concluído</strong> e não poderá mais ser editado."
      confirm-button-text="Sim, encerrar pedido"
      cancel-button-text="Não, voltar"
      @confirmed="onConfirmEncerrarPedido"
    />

    <ConfirmationModal
      ref="confirmacaoReabrirRef"
      title="Reabrir Pedido"
      message="Tem certeza que deseja reabrir este pedido para <strong>Em Digitação</strong>?<br><br>O pedido será reaberto para edição e uma nova solicitação de aprovação comercial será criada."
      confirm-button-text="Sim, reabrir pedido"
      cancel-button-text="Não, voltar"
      @confirmed="onConfirmReabrirPedido"
    />

    <!-- Modal de confirmação genérico para transições de status -->
    <ConfirmationModal
      ref="confirmacaoTransicaoRef"
      title="Confirmação"
      :message="transicaoPendente?.mensagem || 'Tem certeza que deseja prosseguir?'"
      confirm-button-text="Sim, confirmar"
      cancel-button-text="Cancelar"
      @confirmed="onConfirmTransicao"
    />

    <!-- MODAIS DE CONDIÇÃO DE PAGAMENTO -->
    <ModalFormulario
      v-model="showModalCondicao"
      :titulo="condicaoEmEdicao?.idPedidoVendaCondicaoPg ? 'Editar Condição de Pagamento' : 'Adicionar Condição de Pagamento'"
      tamanho="lg"
      :ocultar-confirmar="isFormBloqueado"
      @confirmar="salvarCondicao"
    >
      <div v-if="condicaoEmEdicao" class="row g-3">
        <div class="col-md-3">
          <CampoInput
            id="prazoDias"
            label="Prazo em Dias"
            v-model="condicaoEmEdicao.prazoDias"
            type="number"
            step="1"
            min="0"
            required
          />
        </div>
        <div class="col-md-3">
          <CampoSelect
            id="lstCondPgMomento"
            label="Momento"
            v-model="condicaoEmEdicao.lstCondPgMomento"
            :options="listas.LstCondPgMomento || []"
            option-value="idListaSistemaItem"
            option-label="textoLista"
            required
          />
        </div>
        <div class="col-md-6">
          <CampoBuscaLista
            id="lstReferenciaTemporal"
            label="Referência Temporal"
            v-model="condicaoEmEdicao.lstReferenciaTemporal"
            tabela="PedidoVendaCondicaoPg"
            required
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="percentualDoTotal"
            label="% do Total"
            v-model="condicaoEmEdicao.percentualDoTotal"
            type="moeda"
            required
          />
        </div>
        <div class="col-12 mt-3">
          <div class="alert alert-info py-2 mb-0">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Descrição:</strong> {{ gerarDescricaoCondicao(condicaoEmEdicao) }}
          </div>
        </div>
      </div>
    </ModalFormulario>

    <ConfirmationModal
      ref="modalExclusaoCondicaoRef"
      title="Excluir Condição de Pagamento"
      message="Deseja realmente remover esta condição de pagamento?"
      @confirmed="confirmarExclusaoCondicao"
    />

    <!-- MODAL DE COTAÇÃO MANUAL -->
    <ModalFormulario
      v-model="isModalCotacaoManualOpen"
      titulo="Definir Taxas de Câmbio Manualmente"
      tamanho="xs"
      textoConfirmar="Salvar Taxas"
      @confirmar="salvarCotacaoManual"
    >
      <div class="d-flex flex-column gap-2">
        <CampoInput id="modalTaxaCambioPadrao"
          :label="labelTaxaPadrao"
          type="moeda" :casas-decimais="6"
          v-model="cotacaoManualData.taxaCambioPadrao" />
        <CampoInput id="modalTaxaCambioGerencialPadrao"
          :label="labelTaxaGerencialPadrao"
          type="moeda" :casas-decimais="6"
          v-model="cotacaoManualData.taxaCambioGerencialPadrao" />
        <CampoInput id="modalTaxaCambioGerencial"
          :label="labelTaxaCambioGerencial"
          type="moeda" :casas-decimais="6"
          :model-value="cotacaoManualData.taxaCambioGerencial" disabled />
      </div>
      <div class="mt-2 text-muted small">
        <i class="bi bi-info-circle me-1"></i>
        O campo "{{ labelTaxaCambioGerencial }}" é calculado automaticamente:
        {{ labelTaxaPadrao }} &divide; {{ labelTaxaGerencialPadrao }}
      </div>
    </ModalFormulario>
  </div>
</template>

<style scoped>
.pedido-venda-cadastro-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

.grid-scroll-container {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: auto;
}

.admin-table {
  width: 100%;
  table-layout: fixed;
}

.admin-table th,
.admin-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
}

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}

.admin-table td {
  border-bottom: 1px solid var(--bs-border-color);
  color: var(--bs-body-color);
  padding: 0.5rem 0.75rem;
}

.linha-selecionada td {
  background-color: var(--bs-success-bg-subtle) !important;
  color: var(--bs-success-text-emphasis) !important;
}

.linha-selecionada code {
  color: var(--bs-success-text-emphasis) !important;
}

/* Bloqueio visual removido: a desabilitação é feita campo a campo
   via :disabled nas props individuais, sem escurecer o frame inteiro.
.frame-bloqueado {
  opacity: 0.6;
  filter: grayscale(0.5);
  transition: all 0.3s ease;
}

/* Label do switch "Operação Triangular" — menor que o label do campo */
.label-operacao-triangular {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
}

/* Nav-tabs estilo PurchaseOrder, adaptado para success */
.nav-tabs .nav-link {
  border: none;
  color: var(--bs-secondary-color);
  font-weight: 500;
  padding: 0.75rem 1rem;
  min-width: 130px;
  text-align: center;
  transition: all 0.2s ease;
}

.nav-tabs .nav-link:hover {
  color: var(--bs-success-text-emphasis);
  background-color: rgba(var(--bs-success-rgb), 0.05);
}

.nav-tabs .nav-link.active {
  color: var(--bs-success-text-emphasis);
  background-color: transparent;
  border-bottom: 3px solid var(--bs-success);
  font-weight: 600;
}

@media (max-width: 991.98px) {
  .grid-scroll-container {
    max-height: 150px;
    overflow-x: auto;
  }

  .admin-table {
    min-width: 600px;
  }
}
</style>
